import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ManualMatchingModal from './ManualMatchingModal';
import AdminQuickScoreEntry from './AdminQuickScoreEntry';

const BACKEND_URL = process.env.REACT_APP_API_URL;

interface Squad {
  id: number;
  name: string;
  date: string;
  startTime: string;
  endTime: string;
  gamesPerSquad: number;
  is_position_based?: boolean; // For position-based scheduling
  oil_pattern?: string; // Oil pattern for the squad
}

interface Match {
  id: number;
  squadId: number;
  squadName: string;
  squadDate: string;
  team1Id: number;
  team2Id: number;
  team1Name: string;
  team2Name: string;
  scheduledTime: string;
  result?: string;
  team1Lane?: number;
  team2Lane?: number;
}

interface League {
  name: string;
  startDate: string;
  endDate: string;
  venue?: string;
}

interface ScheduleData {
  league: League;
  squads: Squad[];
  teams: Array<{ id: number; name: string }>;
  matches: Match[];
  eligibleTeamsCount: number;
}

interface ParametricValidation {
  isValid: boolean;
  error?: string;
  teamCount: number;
  squadCount: number;
  maxVersusCount: number;
  requiredMatches: number;
  totalPossibleMatches: number;
}

interface ManageScheduleProps {
  leagueId: number;
  onBack: () => void;
  onNavigateToScoring?: (matchId: number) => void;
}

const ManageSchedule: React.FC<ManageScheduleProps> = ({ leagueId, onBack, onNavigateToScoring }) => {
  const { t } = useTranslation();
  const [scheduleData, setScheduleData] = useState<ScheduleData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showParametricModal, setShowParametricModal] = useState(false);
  const [versusCount, setVersusCount] = useState(1);
  const [parametricValidation, setParametricValidation] = useState<ParametricValidation | null>(null);
  const [scheduling, setScheduling] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  
  // Ref for error message auto-scroll
  const errorRef = React.useRef<HTMLDivElement>(null);
  
  // Position-based scheduling state
  const [schedulingType, setSchedulingType] = useState<'standard' | 'finals_by_position' | 'periodic_by_position' | 'always_by_position'>('standard');
  const [positionFrequency, setPositionFrequency] = useState(4);
  const [byeWeeks, setByeWeeks] = useState<string[]>([]); // Array of date strings for bye weeks
  const [showPackageReminderModal, setShowPackageReminderModal] = useState(false);
  const [packageReminderData, setPackageReminderData] = useState<{weeksAdded: number, newEndDate: string, originalEndDate: string} | null>(null);
  const [selectedSquadMatches, setSelectedSquadMatches] = useState<Match[]>([]);
  const [selectedSquad, setSelectedSquad] = useState<Squad | null>(null);
  const [startLane, setStartLane] = useState(1);
  const [endLane, setEndLane] = useState(2);
  const [lanesAssigned, setLanesAssigned] = useState(false);
  const [assigningLanes, setAssigningLanes] = useState(false);
  const [squadBuildMethod, setSquadBuildMethod] = useState<'new_matchups' | 'copy_week' | 'positions'>('new_matchups');
  const [squadBuildSourceSquadId, setSquadBuildSourceSquadId] = useState<number | null>(null);
  const [buildingSquadMatches, setBuildingSquadMatches] = useState(false);
  const [squadBuildError, setSquadBuildError] = useState<string>('');
  const [showScoresModal, setShowScoresModal] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState<Match | null>(null);
  const [showRemoveLanesModal, setShowRemoveLanesModal] = useState(false);
  const [gamesPerMatch, setGamesPerMatch] = useState(3);
  const [currentGamesCount, setCurrentGamesCount] = useState(0);
  const [savingGames, setSavingGames] = useState(false);
  const [squadGamesCount, setSquadGamesCount] = useState<Record<number, number>>({});
  const [squadHasScores, setSquadHasScores] = useState<Record<number, boolean>>({});
  const [matchHasScores, setMatchHasScores] = useState<Record<number, boolean>>({});
  
  // Baker game state
  const [hasBakerGame, setHasBakerGame] = useState(false);
  const [squadHasBaker, setSquadHasBaker] = useState<Record<number, boolean>>({});
  const [savingBaker, setSavingBaker] = useState(false);
  const [showBakerConfirmModal, setShowBakerConfirmModal] = useState(false);
  const [pendingBakerAction, setPendingBakerAction] = useState<'add' | 'remove' | null>(null);
  
  // Manual matching modal state
  const [showManualMatchingModal, setShowManualMatchingModal] = useState(false);
  
  // Payment status for currently selected squad (informational, does not block actions)
  const [squadUnpaidMembers, setSquadUnpaidMembers] = useState<Array<{bowlerId: number; name: string; teamId: number; teamName: string}>>([]);

  // Manual lane inputs: matchId → { team1: string, team2: string }
  const [manualLaneInputs, setManualLaneInputs] = useState<Record<number, { team1: string; team2: string }>>({});
  // True only when the user has explicitly typed in a lane input (not from auto-assign sync)
  const [manualModeActive, setManualModeActive] = useState(false);
  const [savingLaneMatchId, setSavingLaneMatchId] = useState<number | null>(null);
  const [laneConflictError, setLaneConflictError] = useState<string>('');
  // Swap pairs state
  const [swappingMatchId, setSwappingMatchId] = useState<number | null>(null);
  const [swappingLoading, setSwappingLoading] = useState(false);
  
  // Delete squad modal state
  const [showDeleteSquadModal, setShowDeleteSquadModal] = useState(false);
  const [squadToDelete, setSquadToDelete] = useState<Squad | null>(null);
  const [deletingSquad, setDeletingSquad] = useState(false);

  // Alert modal state (replaces browser alerts)
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [alertModalContent, setAlertModalContent] = useState<{ title: string; message: string }>({ title: '', message: '' });

  // Delete all matches confirmation modal state
  const [showDeleteAllMatchesModal, setShowDeleteAllMatchesModal] = useState(false);

  // Quick score entry modal state
  const [showQuickScores, setShowQuickScores] = useState(false);

  // Bot Blind configuration modal state
  const [showBlindConfigModal, setShowBlindConfigModal] = useState(false);
  const [blindConfig, setBlindConfig] = useState<{
    bot_blind_average: number | '';
    bowler_blind_percentage: number | '';
    bowler_blind_include_handicap: boolean;
  }>({ bot_blind_average: '', bowler_blind_percentage: '', bowler_blind_include_handicap: false });
  const [blindConfigSaving, setBlindConfigSaving] = useState(false);
  const [blindConfigError, setBlindConfigError] = useState('');

  const openBlindConfigModal = async () => {
    setBlindConfigError('');
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const res = await fetch(`${apiUrl}/admin/leagues/${leagueId}/handicap-config`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        const cfg = await res.json();
        setBlindConfig({
          bot_blind_average: cfg.bot_blind_average ?? '',
          bowler_blind_percentage: cfg.bowler_blind_percentage ?? '',
          bowler_blind_include_handicap: cfg.bowler_blind_include_handicap || false
        });
      }
    } catch { /* use defaults */ }
    setShowBlindConfigModal(true);
  };

  const saveBlindConfig = async () => {
    setBlindConfigSaving(true);
    setBlindConfigError('');
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const res = await fetch(`${apiUrl}/admin/leagues/${leagueId}/blind-config`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          bot_blind_average: blindConfig.bot_blind_average !== '' ? Number(blindConfig.bot_blind_average) : null,
          bowler_blind_percentage: blindConfig.bowler_blind_percentage !== '' ? Number(blindConfig.bowler_blind_percentage) : null,
          bowler_blind_include_handicap: blindConfig.bowler_blind_include_handicap
        })
      });
      if (res.ok) {
        setShowBlindConfigModal(false);
      } else {
        const err = await res.json();
        setBlindConfigError(err.error || 'Failed to save');
      }
    } catch {
      setBlindConfigError('Network error');
    } finally {
      setBlindConfigSaving(false);
    }
  };

  // Team-diff handicap state (for match card display)
  const [handicapConfig, setHandicapConfig] = useState<{
    handicap_type: 'individual' | 'team_difference';
    team_difference_percentage: number | null;
  } | null>(null);
  const [teamAverages, setTeamAverages] = useState<Record<number, { setAvgTotal: number; calcAvgTotal: number }>>({});

  // Helper function to show alert modal
  const showAlert = (title: string, message: string) => {
    setAlertModalContent({ title, message });
    setShowAlertModal(true);
  };

  // Fetch handicap config + team averages for team-diff league display
  const fetchHandicapData = async () => {
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const [cfgRes, avgRes] = await Promise.all([
        fetch(`${apiUrl}/admin/leagues/${leagueId}/handicap-config`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        fetch(`${apiUrl}/admin/leagues/${leagueId}/teams/averages`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        })
      ]);
      if (cfgRes.ok) {
        const cfg = await cfgRes.json();
        setHandicapConfig({
          handicap_type: cfg.handicap_type || 'individual',
          team_difference_percentage: cfg.team_difference_percentage ?? null
        });
      }
      if (avgRes.ok) {
        const avgData = await avgRes.json();
        setTeamAverages(avgData.teams || {});
      }
    } catch (e) { console.error('Failed to fetch handicap/averages data:', e); }
  };

  // Auto-scroll to error message when error appears
  React.useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [error]);

  // Fetch schedule data
  const fetchScheduleData = async () => {
    setLoading(true);
    setError('');

    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/schedule/${leagueId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setScheduleData(data);

        // Fetch games count for all squads
        if (data.squads && data.squads.length > 0) {
          const gamesCountPromises = data.squads.map(async (squad: Squad) => {
            try {
              const gamesResponse = await fetch(`${apiUrl}/schedule/${leagueId}/games-count/${squad.id}`, {
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
              });
              if (gamesResponse.ok) {
                const { gamesCount } = await gamesResponse.json();
                return { squadId: squad.id, gamesCount };
              }
            } catch (error) {
              console.error(`Error fetching games for squad ${squad.id}:`, error);
            }
            return { squadId: squad.id, gamesCount: 0 };
          });

          const gamesResults = await Promise.all(gamesCountPromises);
          const gamesCountMap = gamesResults.reduce((acc, result) => {
            acc[result.squadId] = result.gamesCount;
            return acc;
          }, {} as Record<number, number>);

          setSquadGamesCount(gamesCountMap);
        }
      } else {
        const errorData = await response.json();
        setError(errorData.error || t('manageSchedule.errors.failedToFetch'));
      }
    } catch (error) {
      console.error('Fetch schedule data error:', error);
      setError(t('manageSchedule.errors.failedToFetch'));
    } finally {
      setLoading(false);
    }
  };

  // Validate parametric scheduling
  const validateParametricScheduling = async (versus: number) => {
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/schedule/${leagueId}/calculate-parameters?versusCount=${versus}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setParametricValidation(data);
      }
    } catch (error) {
      console.error('Validation error:', error);
    }
  };

  // Schedule matches randomly
  const handleRandomScheduling = async () => {
    if (!scheduleData || scheduleData.eligibleTeamsCount < 2) {
      setError(t('manageSchedule.errors.requiresTwoTeams'));
      return;
    }

    setScheduling(true);
    setError('');

    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/schedule/${leagueId}/random`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        showAlert(t('manageSchedule.modals.alert.successIcon') + ' ' + t('manageSchedule.parametric.modalTitle'), t('manageSchedule.successMessages.matchesScheduledRandom', { count: result.matchesCreated }));
        await fetchScheduleData(); // Refresh data
      } else {
        const errorData = await response.json();
        setError(errorData.error || t('manageSchedule.errors.failedToSchedule'));
      }
    } catch (error) {
      console.error('Random scheduling error:', error);
      setError(t('manageSchedule.errors.failedToScheduleRandom'));
    } finally {
      setScheduling(false);
    }
  };

  // Schedule matches with parameters
  const handleParametricScheduling = async () => {
    if (!scheduleData) {
      return;
    }

    setScheduling(true);
    setError('');

    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      
      // Check if we need to add squads for standard parametric scheduling
      let needsAdditionalWeeks = false;
      let additionalWeeksCount = 0;
      
      if (schedulingType === 'standard' && parametricValidation && !parametricValidation.isValid) {
        // Calculate how many squads we need
        const teamCount = parametricValidation.teamCount;
        const currentSquadCount = parametricValidation.squadCount;
        const matchesPerSquad = Math.floor(teamCount / 2);
        const roundRobinMatches = (teamCount * (teamCount - 1)) / 2;
        const requiredMatches = roundRobinMatches * versusCount;
        const squadsNeeded = Math.ceil(requiredMatches / matchesPerSquad);
        const additionalSquadsNeeded = squadsNeeded - currentSquadCount;

        if (additionalSquadsNeeded > 0) {
          needsAdditionalWeeks = true;
          additionalWeeksCount = additionalSquadsNeeded;
        }
      }
      
      // Use position-based endpoint for advanced scheduling types
      if (schedulingType !== 'standard') {
        // Calculate if additional weeks are needed
        let needsAdditionalWeeks = false;
        let additionalWeeksCount = 0;
        
        if (schedulingType === 'finals_by_position') {
          // For finals mode: need (versusCount * (n-1) rounds) + 1 finals squad
          // Each round of round-robin requires (n-1) squads for n teams
          const teamCount = scheduleData?.eligibleTeamsCount || 0;
          const currentSquadCount = scheduleData?.squads.length || 0;
          const roundsPerVersus = teamCount - 1; // Number of rounds in one complete round-robin
          const totalRoundsNeeded = roundsPerVersus * versusCount; // Total rounds for all versusCount iterations
          const totalSquadsNeeded = totalRoundsNeeded + 1; // Rounds + 1 finals squad
          
          if (currentSquadCount < totalSquadsNeeded) {
            needsAdditionalWeeks = true;
            additionalWeeksCount = totalSquadsNeeded - currentSquadCount;
          }
        } else if (schedulingType === 'periodic_by_position') {
          // For periodic: versusCount applies to NON-position squads
          // Calculate squads needed for versusCount rounds, then add position squads at frequency
          const teamCount = scheduleData?.eligibleTeamsCount || 0;
          const currentSquadCount = scheduleData?.squads.length || 0;
          const roundsPerVersus = teamCount - 1;
          const totalRoundsNeeded = roundsPerVersus * versusCount;
          
          // Calculate how many position squads we need (every positionFrequency normal squads)
          const positionSquadsNeeded = Math.floor(totalRoundsNeeded / positionFrequency);
          const totalSquadsNeeded = totalRoundsNeeded + positionSquadsNeeded;
          
          if (totalSquadsNeeded > currentSquadCount) {
            needsAdditionalWeeks = true;
            additionalWeeksCount = totalSquadsNeeded - currentSquadCount;
          }
        } else if (schedulingType === 'always_by_position') {
          // For always position-based, calculate total squads needed for versusCount
          const teamCount = scheduleData?.eligibleTeamsCount || 0;
          const currentSquadCount = scheduleData?.squads.length || 0;
          const matchesPerSquad = Math.floor(teamCount / 2);
          const roundRobinMatches = (teamCount * (teamCount - 1)) / 2;
          const requiredMatches = roundRobinMatches * versusCount;
          const totalSquadsNeeded = Math.ceil(requiredMatches / matchesPerSquad);
          
          if (totalSquadsNeeded > currentSquadCount) {
            needsAdditionalWeeks = true;
            additionalWeeksCount = totalSquadsNeeded - currentSquadCount;
          }
        }
        
        const response = await fetch(`${apiUrl}/schedule/${leagueId}/position-based`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({ 
            versusCount,
            schedulingType,
            positionFrequency: schedulingType === 'periodic_by_position' ? positionFrequency : null,
            needsAdditionalWeeks,
            additionalWeeksCount,
            byeWeeks
          })
        });

        if (response.ok) {
          const result = await response.json();
          setShowParametricModal(false);
          
          // Show package reminder if weeks were added
          if (result.needsPackageReminder && result.additionalWeeksAdded > 0) {
            const currentEndDate = scheduleData?.league.endDate || '';
            const newEndDate = new Date(currentEndDate);
            newEndDate.setDate(newEndDate.getDate() + (result.additionalWeeksAdded * 7));
            
            setPackageReminderData({
              weeksAdded: result.additionalWeeksAdded,
              newEndDate: newEndDate.toISOString().split('T')[0],
              originalEndDate: currentEndDate
            });
            setShowPackageReminderModal(true);
          } else {
            showAlert(t('manageSchedule.modals.alert.successIcon'), t('manageSchedule.successMessages.matchesScheduledPosition', { count: result.matchesCreated, type: result.schedulingType.replace(/_/g, ' '), squadCount: result.positionBasedSquadsCount }));
          }
          
          await fetchScheduleData(); // Refresh data
        } else {
          const errorData = await response.json();
          setError(errorData.error || t('manageSchedule.errors.failedToSchedule'));
        }
      } else {
        // Standard parametric scheduling
        const response = await fetch(`${apiUrl}/schedule/${leagueId}/parametric`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({ 
            versusCount,
            needsAdditionalWeeks,
            additionalWeeksCount,
            byeWeeks
          })
        });

        if (response.ok) {
          const result = await response.json();
          setShowParametricModal(false);
          
          // Show package reminder if weeks were added
          if (result.needsPackageReminder && result.additionalWeeksAdded > 0) {
            const currentEndDate = scheduleData?.league.endDate || '';
            const newEndDate = new Date(currentEndDate);
            newEndDate.setDate(newEndDate.getDate() + (result.additionalWeeksAdded * 7));
            
            setPackageReminderData({
              weeksAdded: result.additionalWeeksAdded,
              newEndDate: newEndDate.toISOString().split('T')[0],
              originalEndDate: currentEndDate
            });
            setShowPackageReminderModal(true);
          } else {
            showAlert(t('manageSchedule.modals.alert.successIcon'), t('manageSchedule.successMessages.matchesScheduledParam', { count: result.matchesCreated, versus: result.versusCount }));
          }
          
          await fetchScheduleData(); // Refresh data
        } else {
          const errorData = await response.json();
          setError(errorData.error || t('manageSchedule.errors.failedToSchedule'));
        }
      }
    } catch (error) {
      console.error('Parametric scheduling error:', error);
      setError(t('manageSchedule.errors.failedToScheduleParam'));
    } finally {
      setScheduling(false);
    }
  };

  // Clear all matches
  const handleClearMatches = () => {
    setShowDeleteAllMatchesModal(true);
  };

  // Confirm delete all matches
  const confirmDeleteAllMatches = async () => {
    setShowDeleteAllMatchesModal(false);
    setScheduling(true);

    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/schedule/${leagueId}/matches`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        showAlert(t('manageSchedule.modals.alert.successIcon'), t('manageSchedule.successMessages.matchesDeleted', { count: result.deletedCount }));
        await fetchScheduleData(); // Refresh data
      } else {
        const errorData = await response.json();
        setError(errorData.error || t('manageSchedule.errors.failedToDelete'));
      }
    } catch (error) {
      console.error('Clear matches error:', error);
      setError(t('manageSchedule.errors.clearMatches'));
    } finally {
      setScheduling(false);
    }
  };

  // Generate calendar months and dates
  const generateCalendarMonths = () => {
    if (!scheduleData?.league) {
      return [];
    }

    const start = new Date(scheduleData.league.startDate);
    const end = new Date(scheduleData.league.endDate);
    const months = [];

    // Start from the beginning of the start month
    const current = new Date(start.getFullYear(), start.getMonth(), 1);

    while (current <= end) {
      const month = {
        year: current.getFullYear(),
        month: current.getMonth(),
        name: current.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
        dates: [] as Date[]
      };

      // Get all dates in this month
      const monthEnd = new Date(current.getFullYear(), current.getMonth() + 1, 0);
      const dateIterator = new Date(current);

      while (dateIterator <= monthEnd) {
        month.dates.push(new Date(dateIterator));
        dateIterator.setDate(dateIterator.getDate() + 1);
      }

      months.push(month);
      current.setMonth(current.getMonth() + 1);
    }

    return months;
  };

  // Check if a date is within the league date range
  const isDateInLeagueRange = (date: Date) => {
    if (!scheduleData?.league) return false;
    const start = new Date(scheduleData.league.startDate);
    const end = new Date(scheduleData.league.endDate);
    return date >= start && date <= end;
  };

  // Get squads for a specific date
  const getSquadsForDate = (date: Date) => {
    if (!scheduleData) return [];

    const dateStr = date.toISOString().split('T')[0];
    const squads = scheduleData.squads.filter(squad => {
      // Extract just the date part from the squad's timestamp
      const squadDateStr = new Date(squad.date).toISOString().split('T')[0];
      return squadDateStr === dateStr;
    });

    return squads;
  };

  // Helper function to check if a lane is validly assigned (not 0, null, or undefined)
  const isLaneAssigned = (lane: number | undefined | null): lane is number => {
    return lane !== undefined && lane !== null && lane > 0;
  };

  // Check if a squad has scores saved
  const checkSquadHasScores = async (squadId: number): Promise<boolean> => {
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/admin/scoring/squad/${squadId}/has-scores`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        return data.hasScores;
      }
      return false;
    } catch (error) {
      console.error('Error checking squad scores:', error);
      return false;
    }
  };

  // Check if a specific match has scores saved
  const checkMatchHasScores = async (matchId: number): Promise<boolean> => {
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/admin/scoring/match/${matchId}/has-scores`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        return data.hasScores;
      }
      return false;
    } catch (error) {
      console.error('Error checking match scores:', error);
      return false;
    }
  };

  // Check scores for all matches in a squad
  const checkAllMatchScores = async (matches: Match[]) => {
    const scorePromises = matches.map(async (match) => {
      const hasScores = await checkMatchHasScores(match.id);
      return { matchId: match.id, hasScores };
    });

    const results = await Promise.all(scorePromises);
    const scoresMap: Record<number, boolean> = {};
    results.forEach(result => {
      scoresMap[result.matchId] = result.hasScores;
    });

    setMatchHasScores(prev => ({
      ...prev,
      ...scoresMap
    }));
  };

  // Get color class for squad based on its state
  const getSquadColorClass = (squad: Squad) => {
    const matches = getMatchesForSquad(squad.id);

    if (matches.length === 0) {
      // No matches scheduled - orange (includes bye weeks)
      return 'bg-orange-600/30 hover:bg-orange-600/50';
    }

    const hasLanesAssigned = matches.some(match =>
      isLaneAssigned(match.team1Lane) && isLaneAssigned(match.team2Lane)
    );

    // Position-based squads get special colors
    if (squad.is_position_based) {
      if (hasLanesAssigned) {
        // Position-based with lanes assigned - dark blue
        return 'bg-blue-700/40 hover:bg-blue-700/60 border-2 border-blue-800/50';
      }
      // Position-based without lanes - purple
      return 'bg-purple-500/30 hover:bg-purple-500/50 border-2 border-purple-600/50';
    }

    if (!hasLanesAssigned) {
      // Matches scheduled but no lanes assigned - yellow
      return 'bg-yellow-600/30 hover:bg-yellow-600/50';
    }

    // Check if games are set for this squad
    const gamesCount = squadGamesCount[squad.id] || 0;
    if (gamesCount > 0) {
      // Games are set - green
      return 'bg-green-600/30 hover:bg-green-600/50';
    }

    // Lanes assigned but no games set - light blue
    return 'bg-blue-400/30 hover:bg-blue-400/50';
  };

  // Get squad status for display
  const getSquadStatus = (squad: Squad) => {
    const matches = getMatchesForSquad(squad.id);
    const gamesCount = squadGamesCount[squad.id] || 0;

    if (matches.length === 0) {
      return { status: 'no-matches', gamesPerMatch: 0 };
    }

    const hasLanesAssigned = matches.some(match =>
      isLaneAssigned(match.team1Lane) && isLaneAssigned(match.team2Lane)
    );

    if (!hasLanesAssigned) {
      return { status: 'matches-only', gamesPerMatch: 0 };
    }

    if (gamesCount > 0) {
      return { status: 'games-set', gamesPerMatch: gamesCount };
    }

    return { status: 'lanes-assigned', gamesPerMatch: 0 };
  };

  // Get matches for a specific squad
  const getMatchesForSquad = (squadId: number) => {
    if (!scheduleData) return [];
    const matches = scheduleData.matches.filter(match => match.squadId === squadId);
    return matches;
  };

  // Show matches for a squad in the card below calendar
  const showSquadMatches = async (squad: Squad) => {
    const matches = getMatchesForSquad(squad.id);
    setSelectedSquadMatches(matches);
    // Set a selected squad for the card display
    setSelectedSquad(squad);

    // Check if any scores have been saved for this squad
    const hasScores = await checkSquadHasScores(squad.id);
    setSquadHasScores(prev => ({
      ...prev,
      [squad.id]: hasScores
    }));

    // Check if lanes are already assigned for this squad
    const hasLanesAssigned = matches.length > 0 && matches.some(match =>
      isLaneAssigned(match.team1Lane) && isLaneAssigned(match.team2Lane)
    );
    setLanesAssigned(hasLanesAssigned);
    resetManualMode();

    // If lanes are assigned, set the lane range from the first match
    if (hasLanesAssigned && matches.length > 0) {
      const allLanes = matches.flatMap(match => [match.team1Lane, match.team2Lane])
        .filter(isLaneAssigned) as number[];
      if (allLanes.length > 0) {
        setStartLane(Math.min(...allLanes));
        setEndLane(Math.max(...allLanes));
      }
    } else {
      setStartLane(1);
      setEndLane(2);
    }

    // Fetch current games count for this squad
    await fetchCurrentGamesCount(squad.id);
    
    // Fetch baker game status for this squad
    await fetchBakerGameStatus(squad.id);

    // Fetch payment status for this squad (non-blocking, informational)
    if (matches.length > 0) {
      try {
        const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
        const psRes = await fetch(`${apiUrl}/admin/quick-scores/squads/${squad.id}/payment-status`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (psRes.ok) {
          const psData = await psRes.json();
          setSquadUnpaidMembers(psData.unpaidMembers || []);
        } else {
          setSquadUnpaidMembers([]);
        }
      } catch { setSquadUnpaidMembers([]); }
    } else {
      setSquadUnpaidMembers([]);
    }

    // Check scores for all matches in this squad
    if (matches.length > 0) {
      await checkAllMatchScores(matches);
    }

    // Scroll to the squad details card after a short delay to ensure it's rendered
    setTimeout(() => {
      const squadDetailsCard = document.getElementById('squad-details-card');
      if (squadDetailsCard) {
        squadDetailsCard.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest'
        });
      }
    }, 100);
  };

  // Build matches for a specific empty squad (when global scheduling is locked)
  const handleBuildSquadMatches = async () => {
    if (!selectedSquad) return;
    setBuildingSquadMatches(true);
    setSquadBuildError('');
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/schedule/${leagueId}/squad/${selectedSquad.id}/build-matches`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          method: squadBuildMethod,
          sourceSquadId: squadBuildMethod === 'copy_week' ? squadBuildSourceSquadId : undefined
        })
      });
      const result = await response.json();
      if (response.ok) {
        await fetchScheduleData();
        setSquadBuildMethod('new_matchups');
        setSquadBuildSourceSquadId(null);
      } else {
        setSquadBuildError(result.error || 'Failed to build matches');
      }
    } catch {
      setSquadBuildError('Network error. Please try again.');
    } finally {
      setBuildingSquadMatches(false);
    }
  };

  // Assign lanes to matches
  const handleAssignLanes = async () => {
    if (!selectedSquad || selectedSquadMatches.length === 0) return;

    setAssigningLanes(true);

    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/schedule/${leagueId}/assign-lanes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          squadId: selectedSquad.id,
          startLane,
          endLane
        })
      });

      if (response.ok) {
        const result = await response.json();

        // Store current squad ID to maintain selection
        const currentSquadId = selectedSquad.id;

        // Refresh data to get updated lane assignments
        const apiUrlFetch = BACKEND_URL || 'http://localhost:5000/api';
        const refreshResponse = await fetch(`${apiUrlFetch}/schedule/${leagueId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (refreshResponse.ok) {
          const refreshedData = await refreshResponse.json();
          setScheduleData(refreshedData);

          // Get updated matches for the current squad
          const updatedMatches = refreshedData.matches.filter((match: Match) => match.squadId === currentSquadId);
          setSelectedSquadMatches(updatedMatches);

          // Update lane assignment status based on actual data
          const hasLanesAssigned = updatedMatches.length > 0 && updatedMatches.some((match: Match) =>
            isLaneAssigned(match.team1Lane) && isLaneAssigned(match.team2Lane)
          );
          setLanesAssigned(hasLanesAssigned);
          setManualModeActive(false);

          // Update lane range if lanes are assigned
          if (hasLanesAssigned && updatedMatches.length > 0) {
            const allLanes = updatedMatches.flatMap((match: Match) => [match.team1Lane, match.team2Lane])
              .filter(isLaneAssigned) as number[];
            if (allLanes.length > 0) {
              setStartLane(Math.min(...allLanes));
              setEndLane(Math.max(...allLanes));
            }
          }
        }

        // Show warning modal if some teams couldn't be assigned lanes
        // (removed — lanes are always assigned regardless of SquadEntries)

      } else {
        const errorData = await response.json();
        setError(errorData.error || t('manageSchedule.errors.failedToAssignLanes'));
      }
    } catch (error) {
      console.error('Lane assignment error:', error);
      setError(t('manageSchedule.errors.failedToAssignLanes'));
    } finally {
      setAssigningLanes(false);
    }
  };

  // Reshuffle lane assignments
  const handleShuffleLanes = async () => {
    if (!selectedSquad) return;

    // Check if any scores have been saved for this squad
    const hasScores = await checkSquadHasScores(selectedSquad.id);
    if (hasScores) {
      showAlert(t('manageSchedule.alerts.cannotReshuffle'), t('manageSchedule.alerts.cannotReshuffleMessage'));
      return;
    }

    // Proceed with reshuffling
    setAssigningLanes(true);

    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/schedule/${leagueId}/assign-lanes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          squadId: selectedSquad.id,
          startLane,
          endLane,
          reshuffle: true,
          forceRandom: Math.random() // Force different randomization each time
        })
      });

      if (response.ok) {
        await fetchScheduleData(); // Refresh data
        const updatedMatches = getMatchesForSquad(selectedSquad.id);
        setSelectedSquadMatches(updatedMatches);
      } else {
        const errorData = await response.json();
        setError(errorData.error || t('manageSchedule.errors.failedToReshuffle'));
      }
    } catch (error) {
      console.error('Lane reshuffle error:', error);
      setError(t('manageSchedule.errors.failedToReshuffle'));
    } finally {
      setAssigningLanes(false);
    }
  };

  // Remove lane assignments for current squad (only if no games bowled)
  const handleRemoveLanes = async () => {
    if (!selectedSquad) return;

    // Show confirmation modal instead of browser alert
    setShowRemoveLanesModal(true);
  };

  // Confirm and execute lane removal
  const confirmRemoveLanes = async () => {
    if (!selectedSquad) return;

    setShowRemoveLanesModal(false);

    // Check if any scores have been saved for this squad
    const hasScores = await checkSquadHasScores(selectedSquad.id);
    if (hasScores) {
      setError(t('manageSchedule.errors.cannotRemoveLanes'));
      return;
    }

    setAssigningLanes(true);

    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';

      // Proceed with lane removal
      const response = await fetch(`${apiUrl}/schedule/${leagueId}/remove-lanes/${selectedSquad.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        // Reset lane assignment state
        setLanesAssigned(false);
        setStartLane(1);
        setEndLane(2);
        resetManualMode();

        // Refresh schedule data to reflect changes
        await fetchScheduleData();

        // Update the selected squad matches to remove lane assignments
        const updatedMatches = getMatchesForSquad(selectedSquad.id);
        setSelectedSquadMatches(updatedMatches);

        // Clear any previous errors and show success message
        setError('');
        // You could add a success state here if you want a green success message

      } else {
        const errorData = await response.json();
        setError(errorData.error || t('manageSchedule.errors.failedToRemoveLanes'));
      }
    } catch (error) {
      console.error('Remove lanes error:', error);
      setError(t('manageSchedule.errors.failedToRemoveLanes'));
    } finally {
      setAssigningLanes(false);
    }
  };

  // Handle delete squad button click
  const handleDeleteSquadClick = (squad: Squad) => {
    setSquadToDelete(squad);
    setShowDeleteSquadModal(true);
  };

  // Confirm and execute squad deletion
  const confirmDeleteSquad = async () => {
    if (!squadToDelete) return;

    setDeletingSquad(true);
    setShowDeleteSquadModal(false);

    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/schedule/${leagueId}/squad/${squadToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        // Refresh schedule data to show updated squad list
        await fetchScheduleData();
        
        // Clear selection if the deleted squad was selected
        if (selectedSquad?.id === squadToDelete.id) {
          setSelectedSquad(null);
          setSelectedSquadMatches([]);
          setSquadUnpaidMembers([]);
        }
        
        setSquadToDelete(null);
        setError('');
      } else {
        const errorData = await response.json();
        setError(errorData.message || errorData.error || t('manageSchedule.errors.failedToDeleteSquad'));
      }
    } catch (error) {
      console.error('Delete squad error:', error);
      setError(t('manageSchedule.errors.failedToDeleteSquad'));
    } finally {
      setDeletingSquad(false);
    }
  };

  // Check if any lanes have been assigned in the league
  const hasAnyLanesAssigned = () => {
    if (!scheduleData) return false;
    return scheduleData.matches.some(match =>
      isLaneAssigned(match.team1Lane) && isLaneAssigned(match.team2Lane)
    );
  };

  // Fetch current games count for selected squad
  const fetchCurrentGamesCount = async (squadId: number) => {
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/schedule/${leagueId}/games-count/${squadId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const { gamesCount } = await response.json();
        setCurrentGamesCount(gamesCount);
        setGamesPerMatch(Math.max(gamesCount, 1)); // Set input to current count or minimum 1

        // Update the squadGamesCount state
        setSquadGamesCount(prev => ({
          ...prev,
          [squadId]: gamesCount
        }));
      }
    } catch (error) {
      console.error('Error fetching games count:', error);
      setCurrentGamesCount(0);
      setGamesPerMatch(3); // Default to 3 games
    }
  };

  // Save games for selected squad
  const handleSaveGames = async () => {
    if (!selectedSquad || gamesPerMatch < 1 || gamesPerMatch > 10) return;

    // Check if any scores have been saved for this squad
    const hasScores = await checkSquadHasScores(selectedSquad.id);
    if (hasScores) {
      showAlert('Cannot Change Games Count', 'Scores have already been saved for this squad. Game count cannot be changed.');
      return;
    }

    setSavingGames(true);

    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/schedule/${leagueId}/set-games`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          squadId: selectedSquad.id,
          gamesPerMatch
        })
      });

      if (response.ok) {
        const result = await response.json();
        setCurrentGamesCount(result.gamesCount);

        // Update the squadGamesCount state to reflect the change
        setSquadGamesCount(prev => ({
          ...prev,
          [selectedSquad.id]: result.gamesCount
        }));

        setError(''); // Clear any previous errors
      } else {
        const errorData = await response.json();
        setError(errorData.error || t('manageSchedule.errors.failedToSaveGames'));
      }
    } catch (error) {
      console.error('Save games error:', error);
      setError(t('manageSchedule.errors.failedToSaveGames'));
    } finally {
      setSavingGames(false);
    }
  };

  // Handle baker game toggle
  const handleBakerGameToggle = async (add: boolean) => {
    if (!selectedSquad) return;

    // Check if scores have been saved
    if (squadHasScores[selectedSquad.id]) {
      showAlert('Cannot Modify Baker Game', 'Scores have already been saved for this squad. Baker game cannot be modified.');
      return;
    }

    setSavingBaker(true);

    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const endpoint = add ? 'add-baker-game' : 'remove-baker-game';
      
      const response = await fetch(`${apiUrl}/schedule/${leagueId}/${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          squadId: selectedSquad.id
        })
      });

      if (response.ok) {
        const result = await response.json();
        
        // Update squad baker status
        setSquadHasBaker(prev => ({
          ...prev,
          [selectedSquad.id]: add
        }));
        
        setHasBakerGame(add);
        setError('');
      } else {
        const errorData = await response.json();
        setError(errorData.error || `Failed to ${add ? 'add' : 'remove'} baker game`);
      }
    } catch (error) {
      console.error('Baker game error:', error);
      setError(`Failed to ${add ? 'add' : 'remove'} baker game`);
    } finally {
      setSavingBaker(false);
    }
  };

  // Fetch baker game status for selected squad
  const fetchBakerGameStatus = async (squadId: number) => {
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/schedule/${leagueId}/baker-game-status/${squadId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const { hasBaker } = await response.json();
        setHasBakerGame(hasBaker);
        setSquadHasBaker(prev => ({
          ...prev,
          [squadId]: hasBaker
        }));
      }
    } catch (error) {
      console.error('Error fetching baker game status:', error);
      setHasBakerGame(false);
    }
  };

  // Navigate to scoring for a match
  const openScoring = (match: Match) => {
    if (onNavigateToScoring) {
      onNavigateToScoring(match.id);
    } else {
      // Fallback to modal if navigation is not provided
      setSelectedMatch(match);
      setShowScoresModal(true);
    }
  };

  // Returns the paired lane number for any given lane (odd → odd+1, even → even-1)
  const getPairedLane = (lane: number): number => lane % 2 === 1 ? lane + 1 : lane - 1;

  // Handle typing in a manual lane input
  const handleLaneInputChange = (matchId: number, team: 'team1' | 'team2', value: string) => {
    const num = parseInt(value);
    const partner = isNaN(num) || num < 1 ? '' : getPairedLane(num).toString();
    setManualModeActive(true);
    setLaneConflictError('');
    setManualLaneInputs(prev => ({
      ...prev,
      [matchId]: {
        team1: team === 'team1' ? value : (value.trim() === '' ? '' : partner),
        team2: team === 'team2' ? value : (value.trim() === '' ? '' : partner),
      },
    }));
  };

  // Save lanes on blur
  const handleLaneInputBlur = async (matchId: number) => {
    if (!selectedSquad) return;
    const inputs = manualLaneInputs[matchId];
    if (!inputs) return;
    const team1Lane = inputs.team1.trim() !== '' ? parseInt(inputs.team1) : null;
    const team2Lane = inputs.team2.trim() !== '' ? parseInt(inputs.team2) : null;
    // Validate numbers
    if ((inputs.team1.trim() !== '' && isNaN(team1Lane as number)) ||
        (inputs.team2.trim() !== '' && isNaN(team2Lane as number))) return;
    // If clearing, skip duplicate check
    if (team1Lane !== null && team2Lane !== null) {
      // Check for duplicate lane pair across other matches in this squad
      const pair = new Set([team1Lane, team2Lane]);
      const duplicate = selectedSquadMatches.find(m => {
        if (m.id === matchId) return false;
        const other = manualLaneInputs[m.id];
        const o1 = other ? parseInt(other.team1) : (m.team1Lane ?? NaN);
        const o2 = other ? parseInt(other.team2) : (m.team2Lane ?? NaN);
        return !isNaN(o1) && !isNaN(o2) && pair.has(o1) && pair.has(o2);
      });
      if (duplicate) {
        setLaneConflictError(`Lane pair ${team1Lane}-${team2Lane} is already assigned to another match. Choose a different pair.`);
        // Clear the conflicting inputs
        setManualLaneInputs(prev => ({ ...prev, [matchId]: { team1: '', team2: '' } }));
        return;
      }
    }
    setSavingLaneMatchId(matchId);
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/schedule/${leagueId}/matches/${matchId}/lanes`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ team1Lane, team2Lane, squadId: selectedSquad.id }),
      });
      if (response.ok) {
        // Update the local match data
        const updatedMatches = selectedSquadMatches.map(m =>
          m.id === matchId ? { ...m, team1Lane: team1Lane ?? undefined, team2Lane: team2Lane ?? undefined } : m
        );
        setSelectedSquadMatches(updatedMatches);
        const anyLaneAssigned = updatedMatches.some(m => isLaneAssigned(m.team1Lane) || isLaneAssigned(m.team2Lane));
        setLanesAssigned(anyLaneAssigned);
      }
    } catch (err) {
      console.error('Failed to save lane assignment:', err);
    } finally {
      setSavingLaneMatchId(null);
    }
  };

  // Reset manual mode when squad changes (called from showSquadMatches)
  const resetManualMode = () => {
    setManualModeActive(false);
    setLaneConflictError('');
    // Do NOT clear manualLaneInputs here — the useEffect on selectedSquadMatches
    // will re-sync inputs from the new squad's match data automatically.
  };

  // Handle swap pair selection
  const handleSwapSelect = (matchId: number) => {
    if (swappingMatchId === null) {
      setSwappingMatchId(matchId);
    } else if (swappingMatchId === matchId) {
      setSwappingMatchId(null); // cancel
    } else {
      // Execute swap
      handleSwapPairs(swappingMatchId, matchId);
    }
  };

  // Swap lane pairs between two matches
  const handleSwapPairs = async (match1Id: number, match2Id: number) => {
    if (!selectedSquad) return;
    setSwappingLoading(true);
    setSwappingMatchId(null);
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/schedule/${leagueId}/swap-lane-pairs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ match1Id, match2Id, squadId: selectedSquad.id }),
      });
      if (response.ok) {
        const result = await response.json();
        // Update local match data with swapped lanes
        setSelectedSquadMatches(prev => prev.map(m => {
          if (m.id === match1Id) return { ...m, team1Lane: result.match1.team1Lane, team2Lane: result.match1.team2Lane };
          if (m.id === match2Id) return { ...m, team1Lane: result.match2.team1Lane, team2Lane: result.match2.team2Lane };
          return m;
        }));
      } else {
        const err = await response.json();
        setError(err.error || 'Failed to swap lane pairs');
      }
    } catch (err) {
      console.error('Swap pairs error:', err);
      setError('Failed to swap lane pairs');
    } finally {
      setSwappingLoading(false);
    }
  };

  useEffect(() => {
    fetchScheduleData();
    fetchHandicapData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leagueId]);

  // Sync manual lane inputs when selected squad matches change
  useEffect(() => {
    if (selectedSquadMatches.length > 0) {
      const inputs: Record<number, { team1: string; team2: string }> = {};
      for (const m of selectedSquadMatches) {
        inputs[m.id] = {
          team1: m.team1Lane != null ? m.team1Lane.toString() : '',
          team2: m.team2Lane != null ? m.team2Lane.toString() : '',
        };
      }
      setManualLaneInputs(inputs);
    } else {
      setManualLaneInputs({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSquadMatches]);

  useEffect(() => {
    if (showParametricModal && scheduleData) {
      validateParametricScheduling(versusCount);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versusCount, showParametricModal, scheduleData]);

  const calendarMonths = generateCalendarMonths();

  function generateScoresTableHTML(
    team1Members: { playerId: number; playerName: string; average: number; handicap: number }[],
    team2Members: { playerId: number; playerName: string; average: number; handicap: number }[],
    scores: Record<string, number | string | undefined>,
    matchResults: {
      gamesCount?: number;
      team1SeriesTotal?: number;
      team1SeriesHandicap?: number;
      team1Points?: number;
      team2SeriesTotal?: number;
      team2SeriesHandicap?: number;
      team2Points?: number;
    } | undefined,
    match: Match,
    bakerScores: Record<string, number | undefined> = {},
    bakerGames: number[] = []
  ) {
    const gamesCount = matchResults?.gamesCount || 3; // fallback to 3 if not provided
    const maxBakerGame = bakerGames.length > 0 ? Math.max(...bakerGames) : 0;
    const totalGamesToRender = Math.max(gamesCount, maxBakerGame);

    // Helper to render a team's table
    const renderTeamTable = (members: { playerId: number; playerName: string; average: number; handicap: number }[], teamName: string, teamLane?: number, points?: number | string) => `
    <div style="margin-bottom:24px; border:1px solid #000; border-radius:8px; background:#f8f8ff; padding:16px; max-width:600px; margin-left:auto; margin-right:auto;">
      <div style="font-size:18px; font-weight:bold; margin-bottom:4px; text-align:center;">
        ${teamName}
      </div>
      <div style="text-align:center; font-size:14px; color:#444; margin-bottom:10px;">
        ${teamLane ? `Lane ${teamLane}` : ''}&nbsp;&nbsp;&nbsp;Points:&nbsp;${(points !== undefined && points !== null && points !== '') ? `<strong>${points}</strong>` : '<span style="display:inline-block;width:60px;border-bottom:1px solid #999;">&nbsp;</span>'}
      </div>
      <table style="width:100%; border-collapse:collapse; margin-bottom:10px;">
        <tr>
          <th>Player</th>
          <th>Avg</th>
          <th>Hdcp</th>
          ${Array.from({ length: totalGamesToRender }, (_, i) => `<th>G${i + 1}</th>`).join('')}
          <th>Series</th>
          <th>Series w/Hdcp</th>
        </tr>
        ${members.map(member => {
      const series = Array.from({ length: totalGamesToRender }, (_, i) => {
        const gameNumber = i + 1;
        // Return empty string for baker games (individual players don't bowl baker)
        if (bakerGames.includes(gameNumber)) return '';
        return scores[`${member.playerId}-${gameNumber}`] ?? '';
      });
      const seriesTotal = series.reduce<number>((sum, val) => {
        const num = typeof val === 'number' ? val : (val ? Number(val) : 0);
        return sum + (Number.isNaN(num) ? 0 : num);
      }, 0);
      const seriesHdcpTotal = seriesTotal + member.handicap * gamesCount;
      return `
            <tr>
              <td>${member.playerName}</td>
              <td>${member.average}</td>
              <td>${member.handicap}</td>
              ${series.map(s => `<td>${s}</td>`).join('')}
              <td>${seriesTotal}</td>
              <td>${seriesHdcpTotal}</td>
            </tr>
          `;
    }).join('')}
      </table>
    </div>
  `;

    return `
    <div style="display:block;">
      ${renderTeamTable(team1Members, match.team1Name, match.team1Lane, matchResults?.team1Points)}
      ${renderTeamTable(team2Members, match.team2Name, match.team2Lane, matchResults?.team2Points)}
    </div>
  `;
  }

  const handlePrintCalendar = () => {
    if (!scheduleData || scheduleData.matches.length === 0) return;

    const leagueName = scheduleData.league.name;
    const leagueVenue = scheduleData.league.venue || '';
    const totalTeams = scheduleData.eligibleTeamsCount;
    const logoUrl = `${window.location.origin}/BowlerProLogo1.png`;

    // Parse dates safely — squad.date may arrive as ISO string already
    const parseDate = (d: string) => {
      const dt = new Date(d);
      return isNaN(dt.getTime()) ? new Date(d.replace('Z', '').split('T')[0]) : dt;
    };
    const leagueStart = parseDate(scheduleData.league.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    const leagueEnd   = parseDate(scheduleData.league.endDate).toLocaleDateString('en-US',   { month: 'short', day: 'numeric', year: 'numeric' });

    const columnsHTML = scheduleData.squads.map((squad, idx) => {
      const matches = getMatchesForSquad(squad.id);
      const sorted = [...matches].sort((a, b) => {
        if (a.team1Lane != null && b.team1Lane != null) return a.team1Lane - b.team1Lane;
        if (a.team1Lane != null) return -1;
        if (b.team1Lane != null) return 1;
        return 0;
      });

      // Parse squad date safely
      const squadDate = parseDate(squad.date);
      const dateStr = squadDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

      const matchRows = sorted.length > 0
        ? sorted.map(m => {
            const laneStr = m.team1Lane != null && m.team2Lane != null
              ? `<div class="lane-info">Lanes ${m.team1Lane} &amp; ${m.team2Lane}</div>`
              : '';
            const team2Name = m.team2Id === 0 ? 'Blind' : m.team2Name;
            return `
              <div class="match-entry">
                <div class="team-name">${m.team1Name}</div>
                <div class="match-divider">vs</div>
                <div class="team-name">${team2Name}</div>
                ${laneStr}
              </div>`;
          }).join('')
        : '<div class="no-matches">No matches</div>';

      return `
        <div class="week-col">
          <div class="week-header">WEEK ${idx + 1}</div>
          <div class="week-subheader">${dateStr}<br/>${squad.startTime}</div>
          ${matchRows}
        </div>`;
    }).join('');

    const calendarHTML = `
<!DOCTYPE html>
<html>
<head>
  <title>Schedule &mdash; ${leagueName}</title>
  <style>
    @page { size: landscape; margin: 0.4in; }
    * { box-sizing: border-box; }
    body { font-family: Arial, sans-serif; color: #000; background: white; margin: 0; padding: 0; }
    .page-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 0; padding-bottom: 10px; border-bottom: 2px solid #222; }
    .header-left { text-align: left; }
    .header-center { flex: 1; text-align: center; padding: 0 12px; }
    .header-right { text-align: right; }
    .total-teams { font-size: 11px; font-weight: bold; color: #333; }
    .total-teams-label { font-size: 9px; color: #666; text-transform: uppercase; letter-spacing: 0.5px; }
    .league-name { font-size: 22px; font-weight: bold; line-height: 1.2; color: #111; }
    .league-dates { font-size: 10px; color: #555; margin-top: 3px; }
    .league-venue { font-size: 10px; color: #444; margin-top: 2px; font-style: italic; }
    .logo-img { height: 44px; width: auto; }
    .subtitle { text-align: center; font-size: 11px; font-weight: 600; letter-spacing: 1px; color: #444; text-transform: uppercase; margin: 8px 0 10px; }
    .calendar-grid { display: flex; gap: 5px; align-items: flex-start; }
    .week-col { flex: 1; min-width: 0; border: 1px solid #bbb; border-radius: 3px; overflow: hidden; }
    .week-header { background: #222; color: white; font-size: 10px; font-weight: bold; text-align: center; padding: 5px 3px 3px; letter-spacing: 0.5px; }
    .week-subheader { background: #efefef; font-size: 7.5px; text-align: center; padding: 2px 3px; color: #444; border-bottom: 1px solid #ccc; line-height: 1.5; }
    .match-entry { padding: 5px 5px 4px; border-bottom: 1px solid #eee; }
    .match-entry:last-child { border-bottom: none; }
    .team-name { font-size: 9px; font-weight: bold; line-height: 1.3; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .match-divider { text-align: center; font-size: 7px; color: #888; line-height: 1.1; }
    .lane-info { font-size: 7px; color: #555; margin-top: 2px; text-align: right; }
    .no-matches { font-size: 8px; color: #aaa; text-align: center; padding: 8px; font-style: italic; }
    @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
  </style>
</head>
<body>
  <div class="page-header">
    <div class="header-left">
      <div class="total-teams-label">Teams</div>
      <div class="total-teams">${totalTeams}</div>
    </div>
    <div class="header-center">
      <div class="league-name">${leagueName}</div>
      <div class="league-dates">${leagueStart} &ndash; ${leagueEnd}</div>
      ${leagueVenue ? `<div class="league-venue">${leagueVenue}</div>` : ''}
    </div>
    <div class="header-right">
      <img src="${logoUrl}" alt="BowlerPro" class="logo-img" onerror="this.style.display='none'" />
    </div>
  </div>
  <div class="subtitle">Matches &amp; Lane Assignment Breakdown</div>
  <div class="calendar-grid">
    ${columnsHTML}
  </div>
</body>
</html>`;

    const win = window.open('', '_blank');
    if (win) {
      win.document.write(calendarHTML);
      win.document.close();
      win.focus();
      setTimeout(() => win.print(), 200);
    }
  };

  const handlePrintAllMatches = async () => {
    if (!selectedSquadMatches || selectedSquadMatches.length === 0) return;
    const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
    const leagueName = scheduleData?.league.name || '';
    const squadName = selectedSquad?.name || '';
    const logoUrl = `${window.location.origin}/BowlerProLogo1.png`;
    let allMatchesHTML = '';
    let matchIdx = 0;
    for (const match of selectedSquadMatches) {
      const [team1Res, team2Res] = await Promise.all([
        fetch(`${apiUrl}/admin/scoring/teams/${match.team1Id}/members`, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }),
        fetch(`${apiUrl}/admin/scoring/teams/${match.team2Id}/members`, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } })
      ]);
      const team1Members = team1Res.ok ? (await team1Res.json()).members : [];
      const team2Members = team2Res.ok ? (await team2Res.json()).members : [];
      const [scoresRes, gamesRes] = await Promise.all([
        fetch(`${apiUrl}/admin/scoring/match/${match.id}/scores`, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }),
        fetch(`${apiUrl}/admin/scoring/match/${match.id}/games`, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } })
      ]);
      const scoresData = scoresRes.ok ? await scoresRes.json() : {};
      const gamesData = gamesRes.ok ? await gamesRes.json() : {};
      const scores = scoresData.scores || {};
      const matchResults = scoresData.matchResults || {};
      const bakerScores = scoresData.bakerScores || {};
      const games = gamesData.games || [];
      const bakerGames = games.filter((g: any) => g.baker === 1).map((g: any) => g.game_number);
      const pageBreak = matchIdx > 0 ? 'page-break-before: always;' : '';
      allMatchesHTML += `
      <div style="${pageBreak} padding-top: ${matchIdx > 0 ? '20px' : '0'};">
        <div style="display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:14px;padding-bottom:10px;border-bottom:2px solid #333;">
          <div><div style="font-size:11px;text-transform:uppercase;letter-spacing:2px;color:#888;">${leagueName}</div>
          <div style="font-size:15px;font-weight:bold;">${squadName}</div></div>
          <img src="${logoUrl}" alt="BowlerPro" style="height:38px;width:auto;" onerror="this.style.display='none'" />
        </div>
        <div style="margin-bottom:30px;">${generateScoresTableHTML(team1Members, team2Members, scores, matchResults, match, bakerScores, bakerGames)}</div>
      </div>`;
      matchIdx++;
    }
    const printHTML = `<!DOCTYPE html><html><head><title>Lane Assignments - ${squadName}</title>
    <style>body{font-family:Arial,sans-serif;margin:0;padding:20px;background:white;color:black;}
    table{width:100%;border-collapse:collapse;margin-bottom:20px;}
    th,td{border:1px solid #000;padding:8px;text-align:center;}th{background:#f0f0f0;}
    @media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact;}}</style></head>
    <body>${allMatchesHTML}</body></html>`;
    const printWindow = window.open('', '_blank');
    if (printWindow) { printWindow.document.write(printHTML); printWindow.document.close(); printWindow.focus(); setTimeout(() => printWindow.print(), 100); }
  };

  // ── Print All Results ─────────────────────────────────────────────────────
  // Portrait layout. Each match = full-width row with team1 LEFT / team2 RIGHT.
  // Matches stack vertically. Button enabled only when all matches are green.
  const handlePrintAllResults = async () => {
    if (!selectedSquad || !selectedSquadMatches || selectedSquadMatches.length === 0) return;

    const apiUrl     = BACKEND_URL || 'http://localhost:5000/api';
    const leagueName = scheduleData?.league.name || '';
    const leagueVenue = scheduleData?.league.venue || '';
    const leagueStart = scheduleData?.league.startDate
      ? new Date(scheduleData.league.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
    const leagueEnd = scheduleData?.league.endDate
      ? new Date(scheduleData.league.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
    const squadName = selectedSquad.name;
    const squadDate = new Date(selectedSquad.date).toLocaleDateString('en-US', {
      weekday: 'long', month: 'long', day: 'numeric', year: 'numeric'
    });
    const logoUrl = `${window.location.origin}/BowlerProLogo1.png`;
    const authH   = { 'Authorization': `Bearer ${localStorage.getItem('token')}` };

    // Fetch all match data in parallel
    const matchDataList = await Promise.all(selectedSquadMatches.map(async match => {
      const isBotBlind = match.team2Id === 0;
      const [t1Res, t2Res, scoresRes] = await Promise.all([
        fetch(`${apiUrl}/admin/scoring/teams/${match.team1Id}/members`, { headers: authH }),
        isBotBlind ? Promise.resolve(null) : fetch(`${apiUrl}/admin/scoring/teams/${match.team2Id}/members`, { headers: authH }),
        fetch(`${apiUrl}/admin/scoring/match/${match.id}/scores`, { headers: authH }),
      ]);
      const team1Members: any[] = t1Res.ok ? ((await t1Res.json()).members || []) : [];
      const team2Members: any[] = (!isBotBlind && t2Res && (t2Res as Response).ok)
        ? ((await (t2Res as Response).json()).members || []) : [];
      const scoresData = scoresRes.ok ? await scoresRes.json() : {};
      return { match, team1Members, team2Members, scoresData };
    }));

    // ── Build HTML for one match (full-width, two columns inside) ─────────
    const buildMatchBlock = (md: typeof matchDataList[0]): string => {
      const { match, team1Members, team2Members, scoresData } = md;
      const isBotBlind = match.team2Id === 0;

      // API returns: scoresData.scores, scoresData.gamesCount,
      // scoresData.teamGameTotals[gameNum] = {team1Scratch, team1Total, team2Scratch, team2Total}
      // scoresData.matchResults = {team1SeriesTotal, team2SeriesTotal, team1Points, team2Points}
      const scores   = (scoresData.scores || {}) as Record<string, number | string>;
      const G        = (scoresData.gamesCount || 3) as number;
      const mr       = (scoresData.matchResults || null) as any;
      const tgt      = (scoresData.teamGameTotals || {}) as Record<number, {team1Scratch:number; team1Total:number; team2Scratch:number; team2Total:number}>;
      const gameNums = Array.from({ length: G }, (_, i) => i + 1);
      // cols: Bowler(1) + G1+G1H + G2+G2H + ... (2*G) + Ser+SH+WkAvg+LgAvg+Hcp (5) = 2G+6
      const colSpan  = 2 * G + 6;

      // Series handicap = sum of per-game handicap totals from teamGameTotals
      const t1SerHc = gameNums.reduce((s, gn) => s + (Number(tgt[gn]?.team1Total) || 0), 0);
      const t2SerHc = gameNums.reduce((s, gn) => s + (Number(tgt[gn]?.team2Total) || 0), 0);

      // ── Column headers ─────────────────────────────────────────────────
      const colHdrHTML = `<tr>
        <th style="text-align:left;padding:2px 4px;font-size:7px;">Bowler</th>
        ${gameNums.map(gn => `
          <th style="padding:2px 2px;font-size:7px;min-width:20px;">G${gn}</th>
          <th style="padding:2px 2px;font-size:7px;min-width:22px;">G${gn}+H</th>`).join('')}
        <th style="padding:2px 2px;font-size:7px;">Ser</th>
        <th style="padding:2px 2px;font-size:7px;">S+H</th>
        <th style="padding:2px 2px;font-size:7px;">WkAvg</th>
        <th style="padding:2px 2px;font-size:7px;">LgAvg</th>
        <th style="padding:2px 2px;font-size:7px;">Hcp</th>
      </tr>`;

      const td = (content: string | number, extra = '') =>
        `<td style="padding:2px 2px;text-align:center;font-size:8px;${extra}">${content}</td>`;
      const tdL = (content: string | number, extra = '') =>
        `<td style="text-align:left;padding:2px 4px;font-size:8px;${extra}">${content}</td>`;
      const topBorder = 'border-top:1.5px solid #555;';

      // ── Bowler rows ─────────────────────────────────────────────────────
      const bowlerRowsHTML = (members: any[], isBot: boolean): string => {
        if (isBot) return `<tr><td colspan="${colSpan}" style="font-style:italic;color:#888;padding:3px 4px;font-size:7.5px;text-align:left;">Bot Blind — virtual opponent</td></tr>`;
        return members.map((m, mi) => {
          const rowBg = mi % 2 === 1 ? 'background:#f8f8f8;' : '';
          const hcp = Number(m.handicap) || 0;
          const gameCells = gameNums.map(gn => {
            const raw = scores[`${m.playerId}-${gn}`];
            const sc  = (raw !== undefined && raw !== '') ? Number(raw) : null;
            const hcg = sc !== null ? sc + hcp : null;
            return td(sc  !== null ? sc  : '–') +
                   td(hcg !== null ? hcg : '–');
          }).join('');
          const serSc = gameNums.reduce((s, gn) => s + (Number(scores[`${m.playerId}-${gn}`]) || 0), 0);
          const serHc = serSc + hcp * G;
          const wkAvg = G > 0 && serSc > 0 ? (serSc / G).toFixed(1) : '–';
          return `<tr style="${rowBg}">
            ${tdL(m.playerName, 'max-width:90px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;')}
            ${gameCells}
            ${td(serSc || '–', 'font-weight:bold;')}
            ${td(serSc > 0 ? serHc : '–')}
            ${td(serSc > 0 ? wkAvg : '–', 'color:#555;')}
            ${td(m.average || '–', 'color:#555;')}
            ${td(hcp, 'color:#555;')}
          </tr>`;
        }).join('');
      };

      // ── Team total row ──────────────────────────────────────────────────
      const teamTotalHTML = (teamKey: 'team1' | 'team2', pts: number | string): string => {
        const gameCells = gameNums.map(gn => {
          const g  = tgt[gn];
          const sc = g ? (teamKey === 'team1' ? g.team1Scratch : g.team2Scratch) : '–';
          const hc = g ? (teamKey === 'team1' ? g.team1Total   : g.team2Total)   : '–';
          return td(sc, `font-weight:bold;${topBorder}`) +
                 td(hc, topBorder);
        }).join('');
        const ser = mr ? (teamKey === 'team1' ? (mr.team1SeriesTotal || 0) : (mr.team2SeriesTotal || 0)) : '–';
        const shc = teamKey === 'team1' ? (t1SerHc || '–') : (t2SerHc || '–');
        return `<tr>
          ${tdL('TEAM TOTAL', `font-weight:bold;${topBorder}`)}
          ${gameCells}
          ${td(ser, `font-weight:bold;${topBorder}`)}
          ${td(shc, topBorder)}
          <td colspan="2" style="padding:2px 3px;text-align:center;font-size:7.5px;${topBorder}">Pts: <strong>${pts !== '' ? pts : '–'}</strong></td>
          <td style="${topBorder}"></td>
        </tr>`;
      };

      const t1Pts = mr?.team1Points ?? '';
      const t2Pts = mr?.team2Points ?? '';
      const t1Lane = match.team1Lane ? ` <span style="font-size:7px;font-weight:normal;color:#666;">Lane ${match.team1Lane}</span>` : '';
      const t2Lane = match.team2Lane ? ` <span style="font-size:7px;font-weight:normal;color:#666;">Lane ${match.team2Lane}</span>` : '';

      // teamTable: NO redundant team-name div — header already shows both names
      const teamTable = (members: any[], isBot: boolean, teamKey: 'team1' | 'team2', pts: number | string) => `
        <div style="flex:1;min-width:0;">
          <table style="width:100%;border-collapse:collapse;">
            <thead>${colHdrHTML}</thead>
            <tbody>
              ${bowlerRowsHTML(isBot ? [] : members, isBot)}
              ${teamTotalHTML(teamKey, pts)}
            </tbody>
          </table>
        </div>`;

      return `
      <div style="break-inside:avoid;border:1px solid #bbb;border-radius:3px;padding:5px 6px;margin-bottom:8px;">
        <div style="display:flex;justify-content:space-between;align-items:center;border-bottom:1px solid #aaa;padding-bottom:3px;margin-bottom:4px;">
          <span style="font-size:9px;font-weight:bold;">${match.team1Name}${t1Lane}</span>
          <span style="font-size:8px;color:#aaa;padding:0 10px;">vs</span>
          <span style="font-size:9px;font-weight:bold;">${isBotBlind ? 'Bot Blind' : match.team2Name}${t2Lane}</span>
        </div>
        <div style="display:flex;gap:8px;align-items:flex-start;">
          ${teamTable(team1Members, false, 'team1', t1Pts)}
          <div style="width:1px;background:#ccc;align-self:stretch;flex-shrink:0;"></div>
          ${teamTable(isBotBlind ? [] : team2Members, isBotBlind, 'team2', t2Pts)}
        </div>
      </div>`;
    };

    const blocksHTML = matchDataList.map(buildMatchBlock).join('');

    const printHTML = `<!DOCTYPE html>
<html><head>
  <title>Week Results — ${squadName}</title>
  <meta charset="utf-8"/>
  <style>
    @page { size: letter portrait; margin: 0.45in 0.4in; }
    * { box-sizing: border-box; }
    body { font-family: Arial, sans-serif; font-size: 8px; color: #000; background: #fff; margin: 0; padding: 0; }
    table { width: 100%; border-collapse: collapse; }
    th, td { border: 1px solid #ccc; padding: 2px 3px; text-align: center; white-space: nowrap; }
    th { font-weight: bold; background: none; }
    @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
  </style>
</head>
<body>
  <div style="display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #333;padding-bottom:6px;margin-bottom:9px;">
    <div>
      <div style="font-size:15px;font-weight:bold;line-height:1.2;">${leagueName}</div>
      <div style="font-size:7.5px;color:#555;margin-top:2px;">${leagueStart}${leagueEnd ? ' – ' + leagueEnd : ''}${leagueVenue ? ' · ' + leagueVenue : ''}</div>
    </div>
    <div style="text-align:center;flex:1;padding:0 12px;">
      <div style="font-size:10px;font-weight:bold;text-transform:uppercase;letter-spacing:1px;">Week Results</div>
      <div style="font-size:8.5px;color:#333;margin-top:2px;">${squadName} · ${squadDate}</div>
    </div>
    <img src="${logoUrl}" alt="BowlerPro" style="height:34px;width:auto;" onerror="this.style.display='none'" />
  </div>

  ${blocksHTML}

  <div style="margin-top:6px;text-align:right;font-size:7px;color:#aaa;">
    Printed ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()} · BowlerPro
  </div>
</body></html>`;

    const pw = window.open('', '_blank');
    if (pw) { pw.document.write(printHTML); pw.document.close(); pw.focus(); setTimeout(() => pw.print(), 150); }
  };



  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-purple-800 flex items-center justify-center">
        <div className="flex items-center space-x-3 text-white">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
          <span>{t('manageSchedule.loading')}</span>
        </div>
      </div>
    );
  }

  const squadIndex = selectedSquad && scheduleData ? scheduleData.squads.findIndex(s => s.id === selectedSquad.id) : -1;
  const totalSquads = scheduleData ? scheduleData.squads.length : 0;
  const hasPrev = squadIndex > 0;
  const hasNext = squadIndex >= 0 && squadIndex < totalSquads - 1;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-purple-800">
      {/* Header */}
      <div className="bg-white/10 backdrop-blur-lg border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-4">
              <button
                onClick={onBack}
                className="text-white hover:text-blue-300 transition duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <h1 className="text-3xl font-bold text-white">{t('manageSchedule.title')}</h1>
              {scheduleData && (
                <span className="text-blue-200">({scheduleData.league.name})</span>
              )}
            </div>
            {/* Print Calendar button in header */}
            <button
              onClick={handlePrintCalendar}
              disabled={!scheduleData || scheduleData.matches.length === 0}
              title={(!scheduleData || scheduleData.matches.length === 0) ? 'No matches scheduled yet' : 'Print full season calendar'}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600/60 hover:bg-purple-600 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg transition duration-200 shadow"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              Print Calendar
            </button>
          </div>
        </div>
      </div>

      {/* Help Card */}

      <div
        className="w-16 h-16 bg-yellow-600/30 rounded-full flex items-center justify-center shadow-lg cursor-pointer fixed bottom-8 right-8 z-50"
        onClick={() => setShowHelpModal(true)}
      >
        <span className="text-5xl text-yellow-300 font-bold">?</span>
      </div>
      {/* Help Modal */}
      {showHelpModal && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50"
          onClick={e => {
            if (e.target === e.currentTarget) setShowHelpModal(false);
          }}
        >
          <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 max-w-lg w-full mx-4 p-8 relative"
            style={{
              maxHeight: '80vh',
              overflowY: 'auto',
              scrollbarWidth: 'thin',
              scrollbarColor: '#6366f1 #18181b'
            }}>
            {/* Cool ? Icon */}
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-blue-600/30 rounded-full flex items-center justify-center shadow-lg">
                <span className="text-5xl text-blue-300 font-bold">?</span>
              </div>
            </div>
            {/* Instructions Body */}
            <h2 className="text-2xl font-bold text-white text-center mb-4">{t('admin.managingLeagueScheduleHelp')}</h2>
            <div className="text-blue-200 text-base whitespace-pre-line">
              {t('admin.managingLeagueScheduleHelpContent')}
            </div>
          
            <button
              onClick={() => setShowHelpModal(false)}
              className="mt-8 w-full bg-blue-600 text-white py-2 rounded-lg font-bold hover:bg-blue-700 transition"
            >
              {t('common.close')}
            </button>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div ref={errorRef} className="mb-6 bg-red-500/20 border border-red-400 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="text-red-400 text-xl">⚠️</div>
                <p className="text-red-200">{error}</p>
              </div>
              <button
                onClick={() => setError('')}
                className="text-red-400 hover:text-red-300 transition-colors p-1 rounded hover:bg-red-500/20"
                title="Dismiss error"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {scheduleData && (
          <>
            {/* Scheduling Controls */}
            <div className="bg-white/10 backdrop-blur-lg rounded-xl p-4 border border-white/20 mb-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-white">{t('manageSchedule.schedulingControls')}</h2>
                <div className="text-sm text-blue-200">
                  {t('manageSchedule.eligibleTeams')}: {scheduleData.eligibleTeamsCount} | {t('manageSchedule.totalMatches')}: {scheduleData.matches.length}
                </div>
              </div>

              <div className="flex flex-wrap gap-4 items-center">
                {hasAnyLanesAssigned() ? (
                  <div className="flex items-center gap-1.5 px-2.5 py-1 bg-blue-500/20 border border-blue-400/50 rounded-full text-xs text-blue-300">
                    🔒 {t('manageSchedule.scheduleLockedWarning')}
                  </div>
                ) : scheduleData.matches.length > 0 ? (
                  // Show only manual matching and clear buttons when matches exist
                  <div className="w-full space-y-3">
                    <div className="bg-orange-500/20 border border-orange-400 rounded-lg p-3">
                      <div className="flex items-center space-x-2">
                        <svg className="w-5 h-5 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.728-.833-2.498 0L4.316 15.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                        <span className="text-orange-200 font-medium">{t('manageSchedule.matchesAlreadyExist')}</span>
                      </div>
                    </div>
                    <div className="flex gap-4">
                      <button
                        onClick={() => setShowManualMatchingModal(true)}
                        disabled={scheduling}
                        className="px-6 py-3 bg-purple-600/50 hover:bg-purple-700/50 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition duration-200 flex items-center space-x-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        <span>{t('manageSchedule.editManualMatches')}</span>
                      </button>

                      <button
                        onClick={handleClearMatches}
                        disabled={scheduling}
                        className="px-6 py-3 bg-red-600/30 hover:bg-red-700/30 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition duration-200 flex items-center space-x-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        <span>{t('manageSchedule.clearAllMatches')}</span>
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <button
                      onClick={handleRandomScheduling}
                      disabled={scheduling || scheduleData.eligibleTeamsCount < 2}
                      className="px-6 py-3 bg-green-600/50 hover:bg-green-700/50 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition duration-200 flex items-center space-x-2"
                    >
                      {scheduling ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          <span>{t('manageSchedule.parametric.scheduling')}</span>
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                          <span>{t('manageSchedule.scheduleRandomly')}</span>
                        </>
                      )}
                    </button>

                    <button
                      onClick={() => setShowParametricModal(true)}
                      disabled={scheduling || scheduleData.eligibleTeamsCount < 2}
                      className="px-6 py-3 bg-blue-600/50 hover:bg-blue-700/50 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition duration-200 flex items-center space-x-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
                      </svg>
                      <span>{t('manageSchedule.scheduleWithParams')}</span>
                    </button>

                    <button
                      onClick={() => setShowManualMatchingModal(true)}
                      disabled={scheduling || scheduleData.eligibleTeamsCount < 2}
                      className="px-6 py-3 bg-purple-600/50 hover:bg-purple-700/50 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition duration-200 flex items-center space-x-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      <span>{t('manageSchedule.setMatchesManually')}</span>
                    </button>
                  </>
                )}
              </div>

            </div>

            {/* Selected Squad Details Card */}
            {selectedSquad && (
              <div id="squad-details-card" className="bg-white/10 backdrop-blur-lg rounded-xl p-4 border border-white/20 mt-2 mb-4">
                {/* Header row: title + week nav + close */}
                <div className="flex items-center justify-between mb-2 gap-3">
                  <h2 className="text-xl font-bold text-white flex-shrink-0">
                    {selectedSquad.name} Details
                  </h2>
                  {/* Week navigation */}
                  <div className="flex items-center gap-2 bg-white/10 rounded-full px-3 py-1 flex-shrink-0">
                    <button
                      onClick={() => showSquadMatches(scheduleData.squads[squadIndex - 1])}
                      disabled={!hasPrev}
                      className="text-white disabled:text-gray-500 hover:text-blue-300 transition text-lg leading-none font-bold disabled:cursor-not-allowed"
                      title="Previous week"
                    >
                      ‹
                    </button>
                    <span className="text-white text-sm font-semibold px-1 select-none">
                      Week {squadIndex + 1} / {totalSquads}
                    </span>
                    <button
                      onClick={() => showSquadMatches(scheduleData.squads[squadIndex + 1])}
                      disabled={!hasNext}
                      className="text-white disabled:text-gray-500 hover:text-blue-300 transition text-lg leading-none font-bold disabled:cursor-not-allowed"
                      title="Next week"
                    >
                      ›
                    </button>
                  </div>
                  <button
                    onClick={() => setSelectedSquad(null)}
                    className="text-gray-300 hover:text-white transition-colors flex-shrink-0"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="mb-2">
                  <p className="text-blue-300 text-sm">
                    📅 {new Date(selectedSquad.date).toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })} • ⏰ {selectedSquad.startTime} - {selectedSquad.endTime}
                  </p>
                  {selectedSquad.oil_pattern && (
                    <p className="text-green-300 text-sm mt-1">
                      🎳 {t('admin.createLeague.steps.squadsStep.oilPattern')}: {selectedSquad.oil_pattern}
                    </p>
                  )}
                </div>

                {/* Lane Assignment Section */}
                {selectedSquadMatches.length > 0 && (() => {
                  return (
                  <div className="bg-white/5 rounded-lg p-3 border border-white/10 mb-2">
                    {laneConflictError && (
                      <div className="mb-2 text-xs text-red-300 bg-red-500/20 rounded px-2 py-1">
                        ⚠️ {laneConflictError}
                      </div>
                    )}
                    {!lanesAssigned ? (
                      <div>
                        {manualModeActive ? (
                          <div className="flex items-center gap-2 text-sm text-yellow-300">
                            <span>✏️</span>
                            <span>Manual lane entry active — automatic assignment disabled. Clear all lane inputs to re-enable.</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-3">
                            <span className="text-white text-sm font-medium">{t('manageSchedule.squad.lanes')}</span>
                            <input
                              type="number"
                              min="1"
                              max="50"
                              value={startLane}
                              onChange={(e) => setStartLane(parseInt(e.target.value) || 1)}
                              className="w-16 px-2 py-1 bg-white/10 border border-white/20 rounded text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                            <span className="text-gray-400">{t('manageSchedule.squad.to')}</span>
                            <input
                              type="number"
                              min={startLane + 1}
                              max="50"
                              value={endLane}
                              onChange={(e) => setEndLane(parseInt(e.target.value) || startLane + 1)}
                              className="w-16 px-2 py-1 bg-white/10 border border-white/20 rounded text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                            {(() => {
                              const requiredPairs = selectedSquadMatches.length;
                              const availablePairs = Math.floor((endLane - startLane + 1) / 2);
                              const isValid = availablePairs >= requiredPairs;
                              return (
                                <span className={`text-xs ${isValid ? 'text-green-300' : 'text-red-300'}`}>
                                  ({t('manageSchedule.squad.availablePairs')} {availablePairs} | {t('manageSchedule.squad.requiredPairs')} {requiredPairs})
                                </span>
                              );
                            })()}
                            <button
                              onClick={handleAssignLanes}
                              disabled={assigningLanes || Math.floor((endLane - startLane + 1) / 2) < selectedSquadMatches.length}
                              className="px-3 py-1 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white text-sm rounded transition duration-200"
                            >
                              {assigningLanes ? t('manageSchedule.squad.assigning') : t('manageSchedule.squad.assign')}
                            </button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <span className="text-green-300 text-sm">{t('manageSchedule.squad.lanesAssigned', { start: startLane, end: endLane })}</span>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setShowQuickScores(true)}
                            className="px-2 py-1 bg-green-600/70 hover:bg-green-600 text-white text-xs rounded transition duration-200 flex items-center gap-1"
                            title="Open quick score entry for this squad"
                          >
                            ⚡ Enter Scores Fast
                          </button>
                          <button
                            onClick={handleShuffleLanes}
                            disabled={assigningLanes || (selectedSquad && squadHasScores[selectedSquad.id])}
                            className="px-2 py-1 bg-blue-600/50 hover:bg-blue-700/50 disabled:opacity-50 text-white text-xs rounded transition duration-200"
                            title={selectedSquad && squadHasScores[selectedSquad.id] ? t('manageSchedule.squad.reshuffleTooltip') : t('manageSchedule.squad.reshuffleTooltipOk')}
                          >
                            {assigningLanes ? '⏳' : '🔄'} {t('manageSchedule.squad.reshuffle')}
                          </button>
                          <button
                            onClick={handleRemoveLanes}
                            disabled={assigningLanes || (selectedSquad && squadHasScores[selectedSquad.id])}
                            className="px-2 py-1 bg-red-600/50 hover:bg-red-700/50 disabled:opacity-50 text-white text-xs rounded transition duration-200"
                            title={selectedSquad && squadHasScores[selectedSquad.id] ? t('manageSchedule.squad.removeTooltip') : t('manageSchedule.squad.removeTooltipOk')}
                          >
                            {assigningLanes ? '⏳' : '🗑️'} {t('manageSchedule.squad.remove')}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  );
                })()}

                {selectedSquadMatches.length > 0 ? (
                  <div className="space-y-3">
                    <div className="flex items-center  mb-1">
                      <h3 className="text-lg font-semibold text-white">
                        {t('manageSchedule.matches.scheduledMatches')} {t('manageSchedule.matches.count', { count: selectedSquadMatches.length })}
                      </h3>
                      <div className="flex items-center gap-2 ml-4">
                        <span className="text-sm text-blue-300">{t('manageSchedule.matches.gamesPerMatch')}</span>
                        <input
                          type="number"
                          min="1"
                          max="10"
                          value={gamesPerMatch}
                          onChange={(e) => setGamesPerMatch(parseInt(e.target.value) || 1)}
                          disabled={selectedSquad && squadHasScores[selectedSquad.id]}
                          className={`w-16 px-2 py-1 bg-white/10 border rounded text-white text-sm focus:outline-none focus:ring-1 ${selectedSquad && squadHasScores[selectedSquad.id]
                            ? 'border-gray-500 opacity-50 cursor-not-allowed'
                            : gamesPerMatch >= 1 && gamesPerMatch <= 10 && gamesPerMatch !== currentGamesCount
                              ? 'border-blue-400 focus:ring-blue-500'
                              : gamesPerMatch !== currentGamesCount
                                ? 'border-red-400 focus:ring-red-500'
                                : 'border-green-400 focus:ring-green-500'
                            }`}
                          title={selectedSquad && squadHasScores[selectedSquad.id] ? t('manageSchedule.matches.gamesCountTooltip') : ""}
                        />
                        {gamesPerMatch >= 1 && gamesPerMatch <= 10 && gamesPerMatch !== currentGamesCount && !(selectedSquad && squadHasScores[selectedSquad.id]) && (
                          <button
                            onClick={handleSaveGames}
                            disabled={savingGames}
                            className="px-2 py-1 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white text-xs rounded transition duration-200 flex items-center gap-1"
                          >
                            {savingGames ? (
                              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                            ) : (
                              '💾'
                            )}
                            {t('manageSchedule.matches.save')}
                          </button>
                        )}
                        {currentGamesCount > 0 && gamesPerMatch === currentGamesCount && (
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-green-300 bg-green-500/20 px-2 py-1 rounded">
                              {t('manageSchedule.matches.gamesSet', { count: currentGamesCount })}
                            </span>
                            {selectedSquad && squadHasScores[selectedSquad.id] && (
                              <span className="text-xs text-orange-300 bg-orange-500/20 px-2 py-1 rounded">
                                {t('manageSchedule.matches.scoresSaved')}
                              </span>
                            )}
                          </div>
                        )}
                        {(gamesPerMatch < 1 || gamesPerMatch > 10) && (
                          <span className="text-xs text-red-300 bg-red-500/20 px-2 py-1 rounded">
                            {t('manageSchedule.matches.gamesOnlyWarning')}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Baker Game Section */}
                    <div className="bg-white/5 rounded-lg p-3 border border-white/10 mb-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <label className="flex items-center gap-2 text-white text-sm cursor-pointer"
                            title={t('manageSchedule.matches.bakerGameTooltip')}>
                            <input
                              type="checkbox"
                              checked={hasBakerGame}
                              onChange={(e) => {
                                const shouldAdd = e.target.checked;
                                setPendingBakerAction(shouldAdd ? 'add' : 'remove');
                                setShowBakerConfirmModal(true);
                              }}
                              disabled={selectedSquad && squadHasScores[selectedSquad.id]}
                              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            />
                            <span className="font-medium">{t('manageSchedule.matches.bakerGame')}</span>
                          </label>
                          {hasBakerGame && (
                            <span className="text-xs text-green-300 bg-green-500/20 px-2 py-1 rounded">
                              {t('manageSchedule.matches.bakerGameAdded')}
                            </span>
                          )}
                          {savingBaker && (
                            <div className="flex items-center gap-1 text-xs text-blue-300">
                              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-300"></div>
                              <span>Saving...</span>
                            </div>
                          )}
                        </div>
                        {selectedSquad && squadHasScores[selectedSquad.id] && (
                          <span className="text-xs text-gray-400 italic">
                            Cannot modify - scores saved
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex gap-2 mb-1">
                      <button
                        className="px-2 py-2 text-xs bg-purple-600/50 hover:bg-purple-700/50 text-white rounded-lg font-semibold transition"
                        onClick={handlePrintAllMatches}
                      >
                        🖨️ Print Lane Assignments
                      </button>
                      <button
                        className="px-2 py-2 text-xs bg-green-600/50 hover:bg-green-700/50 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-lg font-semibold transition"
                        onClick={handlePrintAllResults}
                        disabled={!selectedSquad || selectedSquadMatches.length === 0 || !selectedSquadMatches.every(m => matchHasScores[m.id])}
                        title={selectedSquadMatches.every(m => matchHasScores[m.id]) ? 'Print all results for this week' : 'Available once all matches have scores'}
                      >
                        📊 Print All Results
                      </button>
                    </div>

                    {/* Unpaid Members Card */}
                    {squadUnpaidMembers.length > 0 && (
                      <div className="mb-3 px-4 py-3 bg-orange-500/10 border border-orange-500/20 rounded-xl">
                        <p className="text-orange-400 text-xs font-semibold mb-1">⚠ Members with outstanding payments</p>
                        <div className="flex flex-wrap gap-1">
                          {squadUnpaidMembers.map(m => (
                            <span key={m.bowlerId} className="bg-orange-500/20 text-orange-300 border border-orange-500/30 rounded-full px-2 py-0.5 text-xs">
                              {m.name} <span className="opacity-60">({m.teamName})</span>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {selectedSquadMatches.map((match, index) => {
                      const hasScores = matchHasScores[match.id] || false;
                      const isSwapSelected = swappingMatchId === match.id;
                      const isSwapTarget = swappingMatchId !== null && swappingMatchId !== match.id;
                      const allMatchesHaveLanes = selectedSquadMatches.every(m => {
                        if (m.team2Id === 0) return isLaneAssigned(m.team1Lane);
                        return isLaneAssigned(m.team1Lane) && isLaneAssigned(m.team2Lane);
                      });
                      const cardColorClass = isSwapSelected
                        ? 'bg-yellow-600/30 border-yellow-400/60 hover:bg-yellow-600/40'
                        : isSwapTarget
                        ? 'bg-blue-600/30 border-blue-400/60 hover:bg-blue-600/40'
                        : hasScores
                        ? 'bg-green-600/20 border-green-400/30 hover:bg-green-600/30'
                        : 'bg-red-600/20 border-red-400/30 hover:bg-red-600/30';
                      const matchLabelClass = hasScores
                        ? 'bg-green-500/30 text-green-200'
                        : 'bg-red-500/30 text-red-200';
                      const laneInputs = manualLaneInputs[match.id] || { team1: '', team2: '' };

                      // Team-diff handicap calculations for match card
                      const isTeamDiffCard = handicapConfig?.handicap_type === 'team_difference';
                      const tdPct = handicapConfig?.team_difference_percentage ?? 0;
                      const t1Avgs = teamAverages[match.team1Id];
                      const t2Avgs = match.team2Id !== 0 ? teamAverages[match.team2Id] : null;
                      let tdHdcpAmt = 0;
                      let tdLowerTeam: 'team1' | 'team2' | null = null;
                      if (isTeamDiffCard && t1Avgs && t2Avgs && tdPct > 0) {
                        // Use calc avg when available (week 2+), fall back to set avg for week 1
                        const t1Eff = t1Avgs.calcAvgTotal > 0 ? t1Avgs.calcAvgTotal : t1Avgs.setAvgTotal;
                        const t2Eff = t2Avgs.calcAvgTotal > 0 ? t2Avgs.calcAvgTotal : t2Avgs.setAvgTotal;
                        const setDiff = Math.abs(t1Eff - t2Eff);
                        tdHdcpAmt = Math.floor(setDiff * tdPct / 100);
                        tdLowerTeam = t1Eff < t2Eff ? 'team1' : t2Eff < t1Eff ? 'team2' : null;
                      }

                      return (
                        <div
                          key={match.id}
                          className={`${cardColorClass} rounded-lg p-4 border cursor-pointer transition duration-200`}
                          onClick={() => {
                            if (swappingMatchId !== null) {
                              handleSwapSelect(match.id);
                            } else {
                              openScoring(match);
                            }
                          }}
                        >
                          {/* Match Status Legend */}
                          <div className="flex items-center gap-4 mb-3 text-xs">
                            <span className="text-gray-300">{t('manageSchedule.matches.matchStatus')}</span>
                            <div className="flex items-center gap-1">
                              <div className="w-3 h-3 bg-green-600 rounded"></div>
                              <span className="text-green-300">{t('manageSchedule.matches.scoresStatus')}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <div className="w-3 h-3 bg-red-600 rounded"></div>
                              <span className="text-red-300">{t('manageSchedule.matches.noScores')}</span>
                            </div>
                          </div>
                          <div className="flex items-center justify-between mb-1">
                            <span className={`${matchLabelClass} px-3 py-1 rounded-full text-sm font-medium`}>
                              {t('manageSchedule.matches.match')} {index + 1} {hasScores ? '✓' : ''}
                            </span>
                            <span className="text-gray-300 text-sm">{match.scheduledTime}</span>
                          </div>
                          {/* Swap Pairs Button */}
                          {allMatchesHaveLanes && !hasScores && (
                            <div className="flex justify-center mb-2">
                              <button
                                onClick={(e) => { e.stopPropagation(); handleSwapSelect(match.id); }}
                                disabled={swappingLoading}
                                className={`text-xs px-3 py-1 rounded transition font-medium ${isSwapSelected ? 'bg-yellow-500 text-black' : 'bg-indigo-600/70 hover:bg-indigo-600 text-white'}`}
                              >
                                {swappingLoading ? '⏳' : isSwapSelected ? '✕ Cancel Swap' : '⇄ Switch Pair'}
                              </button>
                            </div>
                          )}
                          {isSwapTarget && (
                            <div className="text-center text-xs text-blue-300 mb-2 font-medium">
                              Click to swap lanes with selected match
                            </div>
                          )}
                          <div className="flex items-center justify-center">
                            <div className="text-center">
                              <div className="text-md font-semibold text-white">
                                {match.team1Name || `Team ${match.team1Id}`}
                              </div>
                              <div className="mt-1 flex items-center justify-center gap-1" onClick={e => e.stopPropagation()}>
                                <span className="text-xs text-gray-400">LANE:</span>
                                <input
                                  type="text"
                                  inputMode="numeric"
                                  pattern="[0-9]*"
                                  value={laneInputs.team1}
                                  onChange={e => handleLaneInputChange(match.id, 'team1', e.target.value.replace(/\D/g, ''))}
                                  onFocus={e => e.currentTarget.select()}
                                  onBlur={() => handleLaneInputBlur(match.id)}
                                  disabled={hasScores}
                                  placeholder="—"
                                  className="w-12 px-1 py-0.5 text-xs bg-white/10 border border-white/20 rounded text-white text-center focus:outline-none focus:ring-1 focus:ring-blue-400 disabled:opacity-50"
                                />
                                {savingLaneMatchId === match.id && (
                                  <div className="w-3 h-3 border-b-2 border-blue-300 rounded-full animate-spin"></div>
                                )}
                              </div>
                            </div>
                            <div className="mx-6 text-gray-400 font-bold">{t('manageSchedule.matches.vs')}</div>
                            <div className="text-center">
                              <div className="text-md font-semibold text-white flex items-center justify-center gap-1">
                                {match.team2Id === 0 ? (
                                  <>
                                    <span>{t('manualMatching.blind') || 'Blind'}</span>
                                    <button
                                      title="Configure Bot Blind"
                                      onClick={e => { e.stopPropagation(); openBlindConfigModal(); }}
                                      className="text-gray-400 hover:text-white transition"
                                    >
                                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                      </svg>
                                    </button>
                                  </>
                                ) : (match.team2Name || `Team ${match.team2Id}`)}
                              </div>
                              {match.team2Id !== 0 && (
                                <div className="mt-1 flex items-center justify-center gap-1" onClick={e => e.stopPropagation()}>
                                  <span className="text-xs text-gray-400">LANE:</span>
                                  <input
                                    type="text"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    value={laneInputs.team2}
                                    onChange={e => handleLaneInputChange(match.id, 'team2', e.target.value.replace(/\D/g, ''))}
                                    onFocus={e => e.currentTarget.select()}
                                    onBlur={() => handleLaneInputBlur(match.id)}
                                    disabled={hasScores}
                                    placeholder="—"
                                    className="w-12 px-1 py-0.5 text-xs bg-white/10 border border-white/20 rounded text-white text-center focus:outline-none focus:ring-1 focus:ring-blue-400 disabled:opacity-50"
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                          {/* Team-diff avg comparison */}
                          {isTeamDiffCard && t1Avgs && t2Avgs && (
                            <div className="mt-2 pt-2 border-t border-white/10 grid grid-cols-2 gap-x-2 text-xs" onClick={e => e.stopPropagation()}>
                              <div className="text-left space-y-0.5">
                                <div className="text-gray-400">Set Avg: <span className="text-white font-medium">{t1Avgs.setAvgTotal}</span></div>
                                <div className="text-gray-400">Calc Avg: <span className="text-white">{t1Avgs.calcAvgTotal}</span></div>
                                {tdLowerTeam === 'team1' && tdHdcpAmt > 0 && (
                                  <div className="text-green-300 font-semibold">+{tdHdcpAmt} HDCP</div>
                                )}
                              </div>
                              <div className="text-right space-y-0.5">
                                <div className="text-gray-400">Set Avg: <span className="text-white font-medium">{t2Avgs.setAvgTotal}</span></div>
                                <div className="text-gray-400">Calc Avg: <span className="text-white">{t2Avgs.calcAvgTotal}</span></div>
                                {tdLowerTeam === 'team2' && tdHdcpAmt > 0 && (
                                  <div className="text-green-300 font-semibold">+{tdHdcpAmt} HDCP</div>
                                )}
                              </div>
                            </div>
                          )}
                          <div className="text-center mt-1">
                            <span className="text-xs text-gray-400">
                              {t('manageSchedule.matches.clickToViewScores')}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-2">
                    <div className="text-6xl mb-1">📅</div>
                    <p className="text-gray-300">{t('manageSchedule.matches.noMatchesScheduled')}</p>
                    {!hasAnyLanesAssigned() && (
                      <p className="text-sm text-gray-400 mt-1">
                        {t('manageSchedule.matches.useSchedulingTools')}
                      </p>
                    )}
                  </div>
                )}

                {/* Per-squad match builder — shown only when lanes are assigned globally but this squad is empty */}
                {selectedSquadMatches.length === 0 && hasAnyLanesAssigned() && (
                  <div className="mt-4 bg-white/5 rounded-xl p-5 border border-white/10">
                    <h3 className="text-base font-bold text-white mb-3">Build Matches for This Squad</h3>
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      {/* New Matchups */}
                      <button
                        onClick={() => { setSquadBuildMethod('new_matchups'); setSquadBuildError(''); }}
                        className={`p-3 rounded-lg border text-sm font-medium transition text-left ${squadBuildMethod === 'new_matchups' ? 'bg-blue-600/60 border-blue-400 text-white' : 'bg-white/5 border-white/20 text-gray-300 hover:bg-white/10'}`}
                      >
                        <div className="text-2xl mb-1">🔄</div>
                        <div className="font-semibold">New Matchups</div>
                        <div className="text-xs mt-1 opacity-70">Pairs teams that haven't faced each other recently</div>
                      </button>
                      {/* Copy Week */}
                      <button
                        onClick={() => { setSquadBuildMethod('copy_week'); setSquadBuildError(''); }}
                        className={`p-3 rounded-lg border text-sm font-medium transition text-left ${squadBuildMethod === 'copy_week' ? 'bg-purple-600/60 border-purple-400 text-white' : 'bg-white/5 border-white/20 text-gray-300 hover:bg-white/10'}`}
                      >
                        <div className="text-2xl mb-1">📋</div>
                        <div className="font-semibold">Copy Week</div>
                        <div className="text-xs mt-1 opacity-70">Repeat pairings from a previous week</div>
                      </button>
                      {/* By Position */}
                      <button
                        onClick={() => { setSquadBuildMethod('positions'); setSquadBuildError(''); }}
                        className={`p-3 rounded-lg border text-sm font-medium transition text-left ${squadBuildMethod === 'positions' ? 'bg-amber-600/60 border-amber-400 text-white' : 'bg-white/5 border-white/20 text-gray-300 hover:bg-white/10'}`}
                      >
                        <div className="text-2xl mb-1">🏆</div>
                        <div className="font-semibold">By Position</div>
                        <div className="text-xs mt-1 opacity-70">1st vs 2nd, 3rd vs 4th by standings</div>
                      </button>
                      {/* Manual */}
                      <button
                        onClick={() => { setShowManualMatchingModal(true); setSquadBuildError(''); }}
                        className="p-3 rounded-lg border text-sm font-medium transition text-left bg-white/5 border-white/20 text-gray-300 hover:bg-white/10"
                      >
                        <div className="text-2xl mb-1">✏️</div>
                        <div className="font-semibold">Manual</div>
                        <div className="text-xs mt-1 opacity-70">Pick each matchup yourself</div>
                      </button>
                    </div>

                    {/* Source squad selector for copy_week */}
                    {squadBuildMethod === 'copy_week' && (
                      <div className="mb-4">
                        <label className="block text-sm text-gray-300 mb-1">Copy pairings from:</label>
                        <select
                          value={squadBuildSourceSquadId ?? ''}
                          onChange={e => setSquadBuildSourceSquadId(Number(e.target.value) || null)}
                          className="w-full px-3 py-2 bg-gray-800 border border-white/20 rounded-lg text-white text-sm focus:outline-none focus:border-blue-400 [&>option]:bg-gray-800 [&>option]:text-white"
                        >
                          <option value="">— Select a week —</option>
                          {scheduleData?.squads
                            .filter(s => s.id !== selectedSquad?.id && getMatchesForSquad(s.id).length > 0)
                            .map(s => (
                              <option key={s.id} value={s.id}>{(s as any).name || `Squad ${s.id}`} — {(s as any).date}</option>
                            ))}
                        </select>
                      </div>
                    )}

                    {squadBuildError && (
                      <div className="mb-3 px-3 py-2 bg-red-500/20 border border-red-500/30 rounded-lg text-red-300 text-sm">
                        ⚠️ {squadBuildError}
                      </div>
                    )}

                    <button
                      onClick={handleBuildSquadMatches}
                      disabled={buildingSquadMatches || (squadBuildMethod === 'copy_week' && !squadBuildSourceSquadId)}
                      className="w-full px-4 py-3 bg-green-600/50 hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition flex items-center justify-center gap-2"
                    >
                      {buildingSquadMatches ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          Building...
                        </>
                      ) : (
                        '✓ Build Matches'
                      )}
                    </button>
                  </div>
                )}
            </div>
            )}

            {/* Calendar */}
            <div className="space-y-4">
              {calendarMonths.map((month) => (
                <div key={`${month.year}-${month.month}`} className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20">
                  {/* Month Header */}
                  <div className="flex items-center justify-between mb-1">
                    <h2 className="text-xl font-bold text-white">{month.name}</h2>
                    <div className="flex items-center gap-3 text-xs flex-wrap">
                      <div className="flex items-center gap-1">
                        <div className="w-3 h-3 bg-orange-600 rounded"></div>
                        <span className="text-orange-300">{t('manageSchedule.calendar.legend.noMatches')}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-3 h-3 bg-yellow-600 rounded"></div>
                        <span className="text-yellow-300">{t('manageSchedule.calendar.legend.matchesOnly')}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-3 h-3 bg-purple-500 rounded border border-purple-600"></div>
                        <span className="text-purple-300">{t('manageSchedule.calendar.legend.positionBased')}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-3 h-3 bg-blue-400 rounded"></div>
                        <span className="text-blue-300">{t('manageSchedule.calendar.legend.lanesAssigned')}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-3 h-3 bg-blue-700 rounded border border-blue-800"></div>
                        <span className="text-blue-400">{t('manageSchedule.calendar.legend.positionAndLanes')}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-3 h-3 bg-green-600 rounded"></div>
                        <span className="text-green-300">{t('manageSchedule.calendar.legend.gamesSet')}</span>
                      </div>
                      
                    </div>
                  </div>
                  {/* small subtitle as instruction */}
                  <p className="text-sm text-blue-300 m-0 mb-4">
                    {t('manageSchedule.calendar.clickInstruction')}
                  </p>

                  <div className="grid grid-cols-7 gap-2">
                    {/* Calendar Header */}
                    {[t('manageSchedule.calendar.sun'), t('manageSchedule.calendar.mon'), t('manageSchedule.calendar.tue'), t('manageSchedule.calendar.wed'), t('manageSchedule.calendar.thu'), t('manageSchedule.calendar.fri'), t('manageSchedule.calendar.sat')].map(day => (
                      <div key={day} className="text-center text-sm font-semibold text-blue-200 py-2">
                        {day}
                      </div>
                    ))}

                    {/* Empty cells for days before month starts */}
                    {Array.from({ length: month.dates[0].getDay() }, (_, i) => (
                      <div key={`empty-${i}`} className="h-28 min-h-[7rem] bg-white/5 rounded-lg"></div>
                    ))}

                    {/* Calendar Days */}
                    {month.dates.map((date) => {
                      const squads = getSquadsForDate(date);
                      const isInRange = isDateInLeagueRange(date);

                      return (
                        <div
                          key={date.toISOString()}
                          className={`h-28 min-h-[7rem] rounded-lg p-2 transition duration-200 overflow-hidden ${isInRange
                            ? 'bg-white/5 hover:bg-white/10'
                            : 'bg-white/2 opacity-50'
                            }`}
                        >
                          <div className={`text-sm font-medium mb-1 ${isInRange ? 'text-white' : 'text-gray-400'
                            }`}>
                            {date.getDate()}
                          </div>
                          {isInRange && squads.length > 0 && (
                            <div className="space-y-1 overflow-y-auto overflow-x-hidden max-h- pr-1">
                              {squads.map(squad => {
                                const matchCount = getMatchesForSquad(squad.id).length;
                                const colorClass = getSquadColorClass(squad);
                                const squadStatus = getSquadStatus(squad);
                                const canDelete = matchCount === 0; // Only show delete for squads with no matches
                                
                                return (
                                  <div
                                    key={squad.id}
                                    className="relative group "
                                  >
                                    {/* Delete button - only show if squad has no matches */}
                                    {canDelete && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDeleteSquadClick(squad);
                                        }}
                                        className="absolute -top-1 -right-1 z-10 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 shadow-lg"
                                        title={t('manageSchedule.squad.deleteSquadTooltip')}
                                      >
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                      </button>
                                    )}
                                    
                                    <div
                                      onClick={() => showSquadMatches(squad)}
                                      className={`${colorClass} text-xs text-white px-1.5 py-1 rounded cursor-pointer transition duration-200 `}
                                      title={`${squad.name} (${squad.startTime} - ${squad.endTime})${matchCount > 0 ? ` - ${matchCount} ${matchCount === 1 ? t('manageSchedule.calendar.match') : t('manageSchedule.calendar.matches')}` : ''}${squadStatus.gamesPerMatch > 0 ? ` - ${squadStatus.gamesPerMatch} ${t('manageSchedule.calendar.gamesPerMatch')}` : ''}`}
                                    >
                                      <div className="line-clamp-2">
                                        {squad.name}
                                      </div>
                                      {matchCount > 0 && (
                                        <div className={`text-xs mt-0.5 truncate ${colorClass.includes('orange') ? 'text-orange-300' :
                                          colorClass.includes('yellow') ? 'text-yellow-300' :
                                            colorClass.includes('blue') ? 'text-blue-300' :
                                              'text-green-300'
                                          }`}>
                                          {matchCount} match{matchCount !== 1 ? 'es' : ''}
                                          {squadStatus.gamesPerMatch > 0 && (
                                            <span className="block">{squadStatus.gamesPerMatch} games per match</span>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>


          </>
        )}
      </div>

      {/* Parametric Scheduling Modal */}
      {showParametricModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-t-xl">
              <h2 className="text-xl font-bold">{t('manageSchedule.parametric.modalTitle')}</h2>
              <p className="text-blue-100 text-sm mt-1">{t('manageSchedule.parametric.modalSubtitle')}</p>
            </div>

            <div className="p-6 space-y-6">
              {/* Scheduling Type Selection */}
              <div>
                <label className="block text-gray-700 font-medium mb-3">
                  {t('manageSchedule.parametric.schedulingType')}
                </label>
                <div className="space-y-2">
                  <label className="flex items-start p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition">
                    <input
                      type="radio"
                      name="schedulingType"
                      value="standard"
                      checked={schedulingType === 'standard'}
                      onChange={(e) => setSchedulingType(e.target.value as any)}
                      className="mt-1 mr-3"
                    />
                    <div>
                      <div className="font-medium text-gray-900">{t('manageSchedule.parametric.standard.title')}</div>
                      <div className="text-sm text-gray-600">{t('manageSchedule.parametric.standard.description')}</div>
                    </div>
                  </label>

                  <label className="flex items-start p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition">
                    <input
                      type="radio"
                      name="schedulingType"
                      value="finals_by_position"
                      checked={schedulingType === 'finals_by_position'}
                      onChange={(e) => setSchedulingType(e.target.value as any)}
                      className="mt-1 mr-3"
                    />
                    <div>
                      <div className="font-medium text-gray-900">{t('manageSchedule.parametric.finalsByPosition.title')}</div>
                      <div className="text-sm text-gray-600">{t('manageSchedule.parametric.finalsByPosition.description')}</div>
                      <div className="text-xs text-amber-600 mt-1">{t('manageSchedule.parametric.finalsByPosition.warning')}</div>
                    </div>
                  </label>

                  <label className="flex items-start p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition">
                    <input
                      type="radio"
                      name="schedulingType"
                      value="periodic_by_position"
                      checked={schedulingType === 'periodic_by_position'}
                      onChange={(e) => setSchedulingType(e.target.value as any)}
                      className="mt-1 mr-3"
                    />
                    <div>
                      <div className="font-medium text-gray-900">{t('manageSchedule.parametric.periodicByPosition.title')}</div>
                      <div className="text-sm text-gray-600">{t('manageSchedule.parametric.periodicByPosition.description')}</div>
                      <div className="text-xs text-amber-600 mt-1">{t('manageSchedule.parametric.periodicByPosition.warning')}</div>
                    </div>
                  </label>

                  <label className="flex items-start p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition">
                    <input
                      type="radio"
                      name="schedulingType"
                      value="always_by_position"
                      checked={schedulingType === 'always_by_position'}
                      onChange={(e) => setSchedulingType(e.target.value as any)}
                      className="mt-1 mr-3"
                    />
                    <div>
                      <div className="font-medium text-gray-900">{t('manageSchedule.parametric.alwaysByPosition.title')}</div>
                      <div className="text-sm text-gray-600">{t('manageSchedule.parametric.alwaysByPosition.description')}</div>
                      <div className="text-xs text-blue-600 mt-1">{t('manageSchedule.parametric.alwaysByPosition.info')}</div>
                    </div>
                  </label>
                </div>
              </div>

              {/* Position Frequency (only for periodic) */}
              {schedulingType === 'periodic_by_position' && (
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    {t('manageSchedule.parametric.periodicByPosition.frequencyLabel')}
                  </label>
                  <div className="flex items-center space-x-2">
                    <span className="text-gray-600">{t('manageSchedule.parametric.periodicByPosition.every')}</span>
                    <input
                      type="number"
                      min="2"
                      max="10"
                      value={positionFrequency}
                      onChange={(e) => setPositionFrequency(parseInt(e.target.value) || 2)}
                      className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="text-gray-600">{t('manageSchedule.parametric.periodicByPosition.squads')}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {t('manageSchedule.parametric.periodicByPosition.example')}
                  </p>
                </div>
              )}

              {/* Versus Count */}
              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  {t('manageSchedule.parametric.versusCount.label')}
                </label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={versusCount}
                  onChange={(e) => setVersusCount(parseInt(e.target.value) || 1)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={t('manageSchedule.parametric.versusCount.placeholder')}
                />
              </div>

              {/* Bye Weeks Selection */}
              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  🚫 Bye Weeks (Optional)
                </label>
                <p className="text-sm text-gray-600 mb-3">
                  Select dates where no matches should be scheduled. Squads on these dates will remain empty.
                </p>
                <div className="max-h-48 overflow-y-auto border border-gray-300 rounded-lg p-3 bg-gray-50">
                  {scheduleData?.squads && scheduleData.squads.length > 0 ? (
                    <div className="space-y-2">
                      {scheduleData.squads.map((squad) => {
                        const squadDate = new Date(squad.date).toISOString().split('T')[0];
                        const isSelected = byeWeeks.includes(squadDate);
                        const hasMatches = scheduleData.matches.some(m => m.squadId === squad.id);
                        
                        return (
                          <label
                            key={squad.id}
                            className={`flex items-center p-2 rounded cursor-pointer transition ${
                              isSelected ? 'bg-red-100 border border-red-300' : 'hover:bg-gray-100'
                            } ${hasMatches ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              disabled={hasMatches}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setByeWeeks([...byeWeeks, squadDate]);
                                } else {
                                  setByeWeeks(byeWeeks.filter(date => date !== squadDate));
                                }
                              }}
                              className="mr-3 w-4 h-4"
                            />
                            <div className="flex-1">
                              <span className="font-medium text-gray-900">{squad.name}</span>
                              <span className="text-sm text-gray-600 ml-2">
                                {new Date(squad.date).toLocaleDateString('en-US', { 
                                  weekday: 'short', 
                                  month: 'short', 
                                  day: 'numeric' 
                                })}
                              </span>
                              {hasMatches && (
                                <span className="text-xs text-orange-600 ml-2">(has matches)</span>
                              )}
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 text-center py-4">
                      No squads available. Create squads first to select bye weeks.
                    </p>
                  )}
                </div>
                {byeWeeks.length > 0 && (
                  <div className="mt-2 flex items-center justify-between">
                    <p className="text-sm text-gray-700">
                      <span className="font-semibold">{byeWeeks.length}</span> bye week{byeWeeks.length !== 1 ? 's' : ''} selected
                    </p>
                    <button
                      onClick={() => setByeWeeks([])}
                      className="text-xs text-red-600 hover:text-red-800 underline"
                    >
                      Clear all
                    </button>
                  </div>
                )}
              </div>

              {/* Validation Results */}
              {parametricValidation && (
                <div className={`p-3 rounded-lg ${parametricValidation.isValid
                  ? 'bg-green-50 border border-green-200'
                  : 'bg-blue-50 border border-blue-200'
                  }`}>
                  {parametricValidation.isValid ? (
                    <div className="text-green-800">
                      <p className="font-medium">{t('manageSchedule.parametric.validation.validConfig')}</p>
                      <p className="text-sm mt-1">
                        {t('manageSchedule.parametric.validation.matchesScheduled', { count: parametricValidation.requiredMatches, squadCount: parametricValidation.squadCount })}
                      </p>
                    </div>
                  ) : (
                    <div className="text-blue-800">
                      <p className="font-medium">{t('manageSchedule.parametric.validation.additionalSquadsNeeded')}</p>
                      <p className="text-sm mt-1">
                        {(() => {
                          const teamCount = parametricValidation.teamCount;
                          const currentSquadCount = parametricValidation.squadCount;
                          const matchesPerSquad = Math.floor(teamCount / 2);
                          const roundRobinMatches = (teamCount * (teamCount - 1)) / 2;
                          const requiredMatches = roundRobinMatches * versusCount;
                          const squadsNeeded = Math.ceil(requiredMatches / matchesPerSquad);
                          const additionalSquads = squadsNeeded - currentSquadCount;
                          
                          return t('manageSchedule.parametric.validation.additionalSquadsWillBeAdded', { count: additionalSquads, versus: versusCount });
                        })()}
                      </p>
                      <p className="text-xs text-blue-600 mt-1">{t('manageSchedule.parametric.validation.updatePackageCosts')}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Position-based warnings */}
              {schedulingType !== 'standard' && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-900 font-medium mb-2">{t('manageSchedule.parametric.automaticReordering.title')}</p>
                  <ul className="text-xs text-blue-800 space-y-1 list-disc list-inside">
                    <li>{t('manageSchedule.parametric.automaticReordering.list1')}</li>
                    <li>{t('manageSchedule.parametric.automaticReordering.list2')}</li>
                    <li>{t('manageSchedule.parametric.automaticReordering.list3')}</li>
                    {(schedulingType === 'finals_by_position' || schedulingType === 'periodic_by_position') && (
                      <li className="text-amber-700 font-medium">{t('manageSchedule.parametric.automaticReordering.list4')}</li>
                    )}
                  </ul>
                </div>
              )}
            </div>

            <div className="flex space-x-3 p-6 bg-gray-50 rounded-b-xl">
              <button
                onClick={() => setShowParametricModal(false)}
                className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white font-semibold rounded-lg transition duration-200"
              >
                {t('manageSchedule.parametric.cancel')}
              </button>
              <button
                onClick={handleParametricScheduling}
                disabled={!parametricValidation || scheduling}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition duration-200"
              >
                {scheduling ? t('manageSchedule.parametric.scheduling') : t('manageSchedule.parametric.scheduleMatches')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Scores Modal Placeholder */}
      {showScoresModal && selectedMatch && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[80vh] overflow-auto">
            <div className="bg-gradient-to-r from-green-600 to-blue-600 text-white p-6 rounded-t-xl">
              <h2 className="text-xl font-bold">{t('manageSchedule.modals.scoresModal.title')}</h2>
              <p className="text-green-100 mt-1">
                {selectedMatch.team1Name || `Team ${selectedMatch.team1Id}`} {t('manageSchedule.matches.vs')} {selectedMatch.team2Id === 0 ? (t('manualMatching.blind') || 'Blind') : (selectedMatch.team2Name || `Team ${selectedMatch.team2Id}`)}
              </p>
            </div>

            <div className="p-8 text-center">
              <div className="text-6xl mb-4">🎳</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">{t('manageSchedule.modals.scoresModal.scoringSystem')}</h3>
              <p className="text-gray-600 mb-6">
                {t('manageSchedule.modals.scoresModal.description')}
              </p>
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <h4 className="font-semibold text-gray-900 mb-2">{t('manageSchedule.modals.scoresModal.matchDetails')}</h4>
                <p className="text-sm text-gray-600">
                  {t('manageSchedule.modals.scoresModal.scheduledTime')} {selectedMatch.scheduledTime}
                </p>
                {selectedMatch.team1Lane && selectedMatch.team2Lane && (
                  <p className="text-sm text-gray-600">
                    {t('manageSchedule.modals.scoresModal.lanes')} {selectedMatch.team1Lane}-{selectedMatch.team2Lane}
                  </p>
                )}
              </div>
              <p className="text-sm text-gray-500">
                {t('manageSchedule.modals.scoresModal.features')}
              </p>
            </div>

            <div className="p-6 bg-gray-50 rounded-b-xl">
              <button
                onClick={() => setShowScoresModal(false)}
                className="w-full px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white font-semibold rounded-lg transition duration-200"
              >
                {t('manageSchedule.modals.scoresModal.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remove Lane Assignments Confirmation Modal */}
      {showRemoveLanesModal && selectedSquad && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full">
            <div className="bg-gradient-to-r from-red-600 to-red-700 text-white p-6 rounded-t-xl">
              <h2 className="text-xl font-bold flex items-center">
                <span className="text-2xl mr-3">{t('manageSchedule.modals.removeLanes.critical').split(' ')[0]}</span>
                {t('manageSchedule.modals.removeLanes.title')}
              </h2>
              <p className="text-red-100 mt-1">
                {t('manageSchedule.modals.removeLanes.subtitle')}
              </p>
            </div>

            <div className="p-6">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-red-800 mb-2">{t('manageSchedule.modals.removeLanes.critical')}</h3>
                <p className="text-red-700 text-sm mb-3">
                  {t('manageSchedule.modals.removeLanes.willRemoveAll', { squadName: selectedSquad.name })}
                </p>
                <div className="space-y-1 text-red-600 text-sm">
                  <p>{t('manageSchedule.modals.removeLanes.consequence1')}</p>
                  <p>{t('manageSchedule.modals.removeLanes.consequence2')}</p>
                  <p>{t('manageSchedule.modals.removeLanes.consequence3')}</p>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <h4 className="font-semibold text-blue-800 mb-2">{t('manageSchedule.modals.removeLanes.useWhen')}</h4>
                <div className="space-y-1 text-blue-700 text-sm">
                  <p>{t('manageSchedule.modals.removeLanes.useWhen1')}</p>
                  <p>{t('manageSchedule.modals.removeLanes.useWhen2')}</p>
                  <p>{t('manageSchedule.modals.removeLanes.useWhen3')}</p>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowRemoveLanesModal(false)}
                  className="flex-1 px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white font-semibold rounded-lg transition duration-200"
                >
                  {t('manageSchedule.modals.removeLanes.cancel')}
                </button>
                <button
                  onClick={confirmRemoveLanes}
                  className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition duration-200"
                >
                  {t('manageSchedule.modals.removeLanes.confirm')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Package Cost Reminder Modal */}
      {showPackageReminderModal && packageReminderData && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full">
            <div className="bg-gradient-to-r from-amber-500 to-orange-600 text-white p-6 rounded-t-xl">
              <h2 className="text-xl font-bold flex items-center">
                <span className="text-3xl mr-3">{t('manageSchedule.modals.packageReminder.warningIcon')}</span>
                {t('manageSchedule.modals.packageReminder.title')}
              </h2>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h3 className="font-semibold text-green-800 mb-2 flex items-center">
                  <span className="text-xl mr-2">{t('manageSchedule.modals.packageReminder.successIcon')}</span>
                  {t('manageSchedule.modals.packageReminder.successTitle')}
                </h3>
                <p className="text-green-700 text-sm">
                  {t('manageSchedule.modals.packageReminder.successMessage')}
                </p>
              </div>

              <div className="bg-amber-50 border border-amber-300 rounded-lg p-4">
                <h3 className="font-semibold text-amber-900 mb-3">{t('manageSchedule.modals.packageReminder.extendedTitle')}</h3>
                <div className="space-y-2 text-sm text-amber-800">
                  <div className="flex justify-between">
                    <span className="font-medium">{t('manageSchedule.modals.packageReminder.weeksAdded')}</span>
                    <span className="font-bold">{packageReminderData.weeksAdded}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">{t('manageSchedule.modals.packageReminder.originalEndDate')}</span>
                    <span>{new Date(packageReminderData.originalEndDate).toLocaleDateString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">{t('manageSchedule.modals.packageReminder.newEndDate')}</span>
                    <span className="font-bold text-amber-900">{new Date(packageReminderData.newEndDate).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>

              <div className="bg-red-50 border border-red-300 rounded-lg p-4">
                <h3 className="font-semibold text-red-900 mb-2 flex items-center">
                  <span className="text-xl mr-2">{t('manageSchedule.modals.packageReminder.updateCostsIcon')}</span>
                  {t('manageSchedule.modals.packageReminder.updateCostsTitle')}
                </h3>
                <p className="text-red-800 text-sm mb-3">
                  {t('manageSchedule.modals.packageReminder.updateCostsMessage', { count: packageReminderData.weeksAdded })}
                </p>
                <div className="space-y-1 text-red-700 text-xs">
                  <p>{t('manageSchedule.modals.packageReminder.updateStep1')}</p>
                  <p>{t('manageSchedule.modals.packageReminder.updateStep2', { count: packageReminderData.weeksAdded })}</p>
                  <p>{t('manageSchedule.modals.packageReminder.updateStep3')}</p>
                  <p>{t('manageSchedule.modals.packageReminder.updateStep4')}</p>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-800 mb-2">{t('manageSchedule.modals.packageReminder.nextStepsTitle')}</h4>
                <ol className="space-y-1 text-blue-700 text-sm list-decimal list-inside">
                  <li>{t('manageSchedule.modals.packageReminder.nextStep1')}</li>
                  <li>{t('manageSchedule.modals.packageReminder.nextStep2')}</li>
                  <li>{t('manageSchedule.modals.packageReminder.nextStep3')}</li>
                  <li>{t('manageSchedule.modals.packageReminder.nextStep4')}</li>
                </ol>
              </div>
            </div>

            <div className="p-6 bg-gray-50 rounded-b-xl">
              <button
                onClick={() => {
                  setShowPackageReminderModal(false);
                  setPackageReminderData(null);
                }}
                className="w-full px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold rounded-lg transition duration-200"
              >
                {t('manageSchedule.modals.packageReminder.gotIt')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bot Blind Configuration Modal */}
      {showBlindConfigModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 border border-white/20 rounded-2xl shadow-2xl w-full max-w-md">
            <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <h2 className="text-white font-bold text-lg">👻 Bot Blind Configuration</h2>
              </div>
              <button onClick={() => setShowBlindConfigModal(false)} className="text-gray-400 hover:text-white transition">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="px-6 py-5 space-y-5">
              <p className="text-gray-300 text-sm">
                Configure how the Bot Blind team scores are calculated. The bot blind average is used when a team faces the blind — it must be set for points to be awarded.
              </p>

              {/* Bot Blind Average */}
              <div>
                <label className="block text-sm font-medium text-purple-200 mb-1">
                  🤖 Bot Blind Average <span className="text-red-400">*</span>
                </label>
                <input
                  type="number"
                  min="50"
                  max="300"
                  placeholder="e.g. 160"
                  value={blindConfig.bot_blind_average}
                  onChange={e => setBlindConfig(prev => ({ ...prev, bot_blind_average: e.target.value ? parseInt(e.target.value) : '' }))}
                  className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
                <p className="text-gray-400 text-xs mt-1">Score per game for the bot blind team. Required to award points.</p>
              </div>

              {/* Bowler Blind Percentage */}
              <div>
                <label className="block text-sm font-medium text-purple-200 mb-1">
                  👤 Absent Bowler Blind %
                </label>
                <select
                  value={blindConfig.bowler_blind_percentage}
                  onChange={e => setBlindConfig(prev => ({ ...prev, bowler_blind_percentage: e.target.value ? parseFloat(e.target.value) : '' }))}
                  className="w-full px-4 py-2.5 bg-gray-800 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">Not set (use full average)</option>
                  <option value="50">50%</option>
                  <option value="60">60%</option>
                  <option value="70">70%</option>
                  <option value="80">80%</option>
                  <option value="90">90%</option>
                  <option value="100">100%</option>
                </select>
                <p className="text-gray-400 text-xs mt-1">Score used when a real bowler is absent (% of their average).</p>
              </div>

              {/* Include Handicap */}
              <div className="flex items-center gap-3 bg-white/5 rounded-lg px-4 py-3">
                <input
                  type="checkbox"
                  id="blindIncludeHdcp"
                  checked={blindConfig.bowler_blind_include_handicap}
                  onChange={e => setBlindConfig(prev => ({ ...prev, bowler_blind_include_handicap: e.target.checked }))}
                  className="w-4 h-4 rounded text-purple-500 bg-gray-700 border-gray-600 focus:ring-purple-500"
                />
                <label htmlFor="blindIncludeHdcp" className="text-white text-sm cursor-pointer">
                  Include handicap in absent bowler blind score
                </label>
              </div>

              {blindConfigError && (
                <p className="text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">{blindConfigError}</p>
              )}
            </div>

            <div className="px-6 py-4 border-t border-white/10 flex justify-end gap-3">
              <button
                onClick={() => setShowBlindConfigModal(false)}
                className="px-4 py-2 text-gray-300 hover:text-white transition text-sm"
              >
                Cancel
              </button>
              <button
                onClick={saveBlindConfig}
                disabled={blindConfigSaving || blindConfig.bot_blind_average === ''}
                className="px-5 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white rounded-lg text-sm font-semibold transition"
              >
                {blindConfigSaving ? 'Saving…' : 'Save Configuration'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Squad Confirmation Modal */}
      {showDeleteSquadModal && squadToDelete && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="bg-gradient-to-r from-red-500 to-red-600 text-white p-6 rounded-t-xl">
              <div className="flex items-center space-x-3">
                <span className="text-3xl">{t('manageSchedule.modals.deleteSquad.titleIcon')}</span>
                <div>
                  <h2 className="text-xl font-bold">{t('manageSchedule.modals.deleteSquad.title')}</h2>
                  <p className="text-red-100 text-sm mt-1">{t('manageSchedule.modals.deleteSquad.subtitle')}</p>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-800 mb-2">{t('manageSchedule.modals.deleteSquad.squadDetails')}</h4>
                <div className="space-y-1 text-sm text-gray-700">
                  <p><strong>{t('manageSchedule.modals.deleteSquad.name')}</strong> {squadToDelete.name}</p>
                  <p><strong>{t('manageSchedule.modals.deleteSquad.date')}</strong> {new Date(squadToDelete.date).toLocaleDateString()}</p>
                  <p><strong>{t('manageSchedule.modals.deleteSquad.time')}</strong> {squadToDelete.startTime} - {squadToDelete.endTime}</p>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-800 mb-2">{t('manageSchedule.modals.deleteSquad.safetyChecks')}</h4>
                <p className="text-blue-700 text-sm">
                  {t('manageSchedule.modals.deleteSquad.safetyMessage')}
                </p>
                <ul className="space-y-1 text-blue-700 text-sm mt-2 ml-4">
                  <li className="flex items-start">
                    <span className="mr-2"></span> {t('manageSchedule.modals.deleteSquad.check1')}
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2"></span> {t('manageSchedule.modals.deleteSquad.check2')}
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2"></span> {t('manageSchedule.modals.deleteSquad.check3')}
                  </li>
                </ul>
                <p className="text-blue-600 text-xs mt-2">
                  {t('manageSchedule.modals.deleteSquad.safetyNote')}
                </p>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-amber-800 text-sm" dangerouslySetInnerHTML={{ __html: t('manageSchedule.modals.deleteSquad.warning', { squadName: squadToDelete.name }) }} />
              </div>
            </div>

            <div className="p-6 bg-gray-50 rounded-b-xl flex space-x-3">
              <button
                onClick={() => {
                  setShowDeleteSquadModal(false);
                  setSquadToDelete(null);
                }}
                disabled={deletingSquad}
                className="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold rounded-lg transition duration-200 disabled:opacity-50"
              >
                {t('manageSchedule.modals.deleteSquad.cancel')}
              </button>
              <button
                onClick={confirmDeleteSquad}
                disabled={deletingSquad}
                className="flex-1 px-4 py-2 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-semibold rounded-lg transition duration-200 disabled:opacity-50"
              >
                {deletingSquad ? t('manageSchedule.modals.deleteSquad.deleting') : t('manageSchedule.modals.deleteSquad.deleteSquad')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete All Matches Confirmation Modal */}
      {showDeleteAllMatchesModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="bg-gradient-to-r from-red-500 to-red-600 text-white p-6 rounded-t-xl">
              <div className="flex items-center space-x-3">
                <span className="text-3xl">{t('manageSchedule.modals.deleteAllMatches.titleIcon')}</span>
                <div>
                  <h2 className="text-xl font-bold">{t('manageSchedule.modals.deleteAllMatches.title')}</h2>
                  <p className="text-red-100 text-sm mt-1">{t('manageSchedule.modals.deleteAllMatches.subtitle')}</p>
                </div>
              </div>
            </div>

            <div className="p-6">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                <p className="text-amber-800 text-sm" dangerouslySetInnerHTML={{ __html: t('manageSchedule.modals.deleteAllMatches.warning') }} />
              </div>
              <p className="text-gray-700">
                {t('manageSchedule.modals.deleteAllMatches.message')}
              </p>
            </div>

            <div className="p-6 bg-gray-50 rounded-b-xl flex space-x-3">
              <button
                onClick={() => setShowDeleteAllMatchesModal(false)}
                className="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold rounded-lg transition duration-200"
              >
                {t('manageSchedule.modals.deleteAllMatches.cancel')}
              </button>
              <button
                onClick={confirmDeleteAllMatches}
                className="flex-1 px-4 py-2 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-semibold rounded-lg transition duration-200"
              >
                {t('manageSchedule.modals.deleteAllMatches.deleteAll')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Alert Modal (replaces browser alerts) */}
      {showAlertModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-6 rounded-t-xl">
              <div className="flex items-center space-x-3">
                <span className="text-3xl">
                  {alertModalContent.title.toLowerCase().includes('success') ? t('manageSchedule.modals.alert.successIcon') : 
                   alertModalContent.title.toLowerCase().includes('cannot') ? t('manageSchedule.modals.alert.cannotIcon') : t('manageSchedule.modals.alert.infoIcon')}
                </span>
                <div>
                  <h2 className="text-xl font-bold">{alertModalContent.title}</h2>
                </div>
              </div>
            </div>

            <div className="p-6">
              <p className="text-gray-700 whitespace-pre-line">{alertModalContent.message}</p>
            </div>

            <div className="p-6 bg-gray-50 rounded-b-xl">
              <button
                onClick={() => setShowAlertModal(false)}
                className="w-full px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-lg transition duration-200"
              >
                {t('manageSchedule.modals.alert.ok')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Baker Game Confirmation Modal */}
      {showBakerConfirmModal && pendingBakerAction && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className={`${pendingBakerAction === 'add' ? 'bg-gradient-to-r from-green-600 to-blue-600' : 'bg-gradient-to-r from-red-600 to-red-700'} text-white p-6 rounded-t-xl`}>
              <h2 className="text-xl font-bold flex items-center">
                <span className="text-2xl mr-3">{pendingBakerAction === 'add' ? '🎳' : '⚠️'}</span>
                {pendingBakerAction === 'add' ? t('manageSchedule.modals.bakerConfirm.addTitle') : t('manageSchedule.modals.bakerConfirm.removeTitle')}
              </h2>
            </div>

            <div className="p-6">
              <div className={`${pendingBakerAction === 'add' ? 'bg-blue-50 border-blue-200' : 'bg-red-50 border-red-200'} border rounded-lg p-4 mb-4`}>
                <p className={`${pendingBakerAction === 'add' ? 'text-blue-900' : 'text-red-900'} text-sm mb-3`}>
                  {pendingBakerAction === 'add' ? t('manageSchedule.modals.bakerConfirm.addMessage') : t('manageSchedule.modals.bakerConfirm.removeMessage')}
                </p>
                {pendingBakerAction === 'add' && (
                  <div className="bg-blue-100 rounded p-3 mt-3">
                    <p className="text-xs text-blue-800 font-medium mb-1">{t('manageSchedule.modals.bakerConfirm.whatIsBaker')}</p>
                    <p className="text-xs text-blue-700">{t('manageSchedule.matches.bakerGameTooltip')}</p>
                  </div>
                )}
              </div>
              <p className="text-gray-600 text-sm">
                {t('manageSchedule.modals.bakerConfirm.confirm')}
              </p>
            </div>

            <div className="p-6 bg-gray-50 rounded-b-xl flex gap-3">
              <button
                onClick={() => {
                  setShowBakerConfirmModal(false);
                  setPendingBakerAction(null);
                  // Reset checkbox to previous state
                  setHasBakerGame(hasBakerGame);
                }}
                className="flex-1 px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white font-semibold rounded-lg transition duration-200"
              >
                {t('manageSchedule.modals.bakerConfirm.cancel')}
              </button>
              <button
                onClick={async () => {
                  setShowBakerConfirmModal(false);
                  await handleBakerGameToggle(pendingBakerAction === 'add');
                  setPendingBakerAction(null);
                }}
                disabled={savingBaker}
                className={`flex-1 px-4 py-2 ${pendingBakerAction === 'add' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'} disabled:opacity-50 text-white font-semibold rounded-lg transition duration-200`}
              >
                {savingBaker ? t('manageSchedule.modals.bakerConfirm.processing') : t('manageSchedule.modals.bakerConfirm.proceed')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manual Matching Modal */}
      {showManualMatchingModal && scheduleData && (
        <ManualMatchingModal
          leagueId={leagueId}
          teams={scheduleData.teams || []}
          squads={scheduleData.squads}
          existingMatches={scheduleData.matches.map(m => ({
            squadId: m.squadId,
            team1Id: m.team1Id,
            team2Id: m.team2Id,
            team1Name: m.team1Name,
            team2Name: m.team2Name
          }))}
          onClose={() => setShowManualMatchingModal(false)}
          onMatchesSaved={fetchScheduleData}
        />
      )}

      {/* Quick Score Entry Modal */}
      {showQuickScores && selectedSquad && (
        <AdminQuickScoreEntry
          squadId={selectedSquad.id}
          squadName={selectedSquad.name}
          squadDate={selectedSquad.date}
          gamesPerMatch={currentGamesCount > 0 ? currentGamesCount : gamesPerMatch}
          onClose={async () => {
            setShowQuickScores(false);
            await fetchScheduleData();
            // Re-check match scores so the green/red indicators refresh immediately
            if (selectedSquad) {
              const currentMatches = getMatchesForSquad(selectedSquad.id);
              if (currentMatches.length > 0) await checkAllMatchScores(currentMatches);
            }
            setSelectedSquad(null);
          }}
        />
      )}
    </div>
  );
};

export default ManageSchedule;